type EventsShape = Record<
  string,
  Record<string, { name: string; properties: readonly string[]; optionalProperties?: readonly string[] }>
>;

const BuildersGlobalProperties = ['user_id', 'account_id', 'workspace_id', 'account_plan_type'] as const;
export type BuildersGlobalProperty = (typeof BuildersGlobalProperties)[number];

// Shared by every agentUsage event; built by useAgentUsageCommonEventProps in features/billing-usage.
const AgentUsageCommonProperties = [
  'Used Percent Bucket',
  'Projected Percent Bucket',
  'Enforcing',
  'Beta',
  'Account Id',
  'Role',
  'Is Standard Role',
] as const;
export type AgentUsageCommonProperty = (typeof AgentUsageCommonProperties)[number];

// Shared by every run-scoped brand event; built by useBrandRunProps in features/design-studio/brand.
const BrandRunProperties = ['run_id', 'has_document', 'entry', 'elapsed_ms'] as const;
export type BrandRunProperty = (typeof BrandRunProperties)[number];

export const Events = {
  renderer: {
    // The workspace default-template-engine ("liquid version") upgrade/downgrade flow. Values for
    // 'Current Version'/'Version' are the engine name ('ruby' for legacy, 'liquidjs' for latest).
    versionChangeStarted: {
      name: 'Renderer: Version Change Started',
      properties: ['Current Version', 'Workspace Setting'] as const,
    },
    versionChangeCompleted: {
      name: 'Renderer: Version Change Completed',
      properties: ['Version', 'Workspace Setting'] as const,
    },
  },
  role: {
    createStarted: {
      name: 'Role: Custom Role Create Started',
      properties: [] as const,
    },
    duplicateStarted: {
      name: 'Role: Duplicate Started',
      properties: ['Role ID', 'Role Name', 'Role Type', 'Permissions Count'] as const,
    },
    deleteStarted: {
      name: 'Role: Custom Role Delete Started',
      properties: ['Role ID', 'Role Name', 'Role Type', 'Permissions Count'] as const,
    },
  },
  // Data index (the schema browser over attributes, object attributes,
  // relationship attributes and events). `location` says which surface the
  // action was taken from, so the same action from the table, the drawer and
  // the full page can be told apart; `type` is 'attribute' or 'event', with
  // object and relationship attributes reported as attributes.
  dataIndex: {
    descriptionUpdated: {
      name: 'Data Index: Description Updated',
      properties: ['type', 'location'] as const,
    },
    tagsUpdated: {
      name: 'Data Index: Tags Updated',
      properties: ['type', 'location', 'tag_count'] as const,
    },
    viewFullPageClicked: {
      name: 'Data Index: View Full Page Clicked',
      properties: ['type'] as const,
    },
    bulkActionClicked: {
      name: 'Data Index: Bulk Action Clicked',
      properties: ['action', 'item_count', 'type', 'location'] as const,
    },
    itemClicked: {
      name: 'Data Index: Item Clicked',
      properties: ['type', 'location'] as const,
    },
    exportStarted: {
      name: 'Data Index: Export Started',
      properties: ['export_type'] as const,
    },
  },
  // Sending one event for one profile, from the events browser, an event's own
  // page, or the activity feed's Resend. `Source` names the surface; `Is Resend`
  // separates retrying a recorded event from composing a new one.
  events: {
    sendStarted: {
      name: 'Event: Send Started',
      properties: ['Source', 'Is Resend'] as const,
    },
    sendCompleted: {
      name: 'Event: Send Completed',
      properties: [
        'Source',
        'Is Resend',
        'Event Name',
        'Event Attribute Count',
        'Event Attribute Editor Type',
      ] as const,
    },
    errorEncountered: {
      name: 'Event: Error Encountered',
      properties: [
        'Source',
        'Is Resend',
        'Error Message',
        'Event Name',
        'Event Attribute Count',
        'Event Attribute Editor Type',
      ] as const,
    },
  },
  // Agent usage meter events for the pricing-experiment learning plan (TAG-1927).
  // Every event carries the shared slice properties so the analysis can split by
  // usage bucket, 1A/1B enforcement era, and role without per-event fixes.
  agentUsage: {
    planCardViewed: {
      name: 'Agent Usage: Plan Card Viewed',
      properties: [...AgentUsageCommonProperties, 'Cycle Day'] as const,
    },
    aiUsageTabViewed: {
      name: 'Agent Usage: AI Usage Tab Viewed',
      properties: [...AgentUsageCommonProperties, 'Active Users', 'People Rows Shown', 'Folded Rows'] as const,
    },
    unitToggled: {
      name: 'Agent Usage: Unit Toggled',
      properties: [...AgentUsageCommonProperties, 'To Unit'] as const,
    },
    chatPillShown: {
      name: 'Agent Usage: Chat Pill Shown',
      properties: [...AgentUsageCommonProperties, 'Critical'] as const,
    },
    chatPillTooltipOpened: {
      name: 'Agent Usage: Chat Pill Tooltip Opened',
      properties: [...AgentUsageCommonProperties] as const,
    },
    threadCardShown: {
      name: 'Agent Usage: Thread Card Shown',
      properties: [...AgentUsageCommonProperties, 'Variant'] as const,
    },
  },
  aiAssistant: {
    // Exposure event: which launcher arm ('bar' | 'footer') the user was served,
    // fired once per app load regardless of whether they open the agent. The
    // denominator for open-rate comparisons across the agent_floating_launcher
    // rollout — `opened` alone only counts users who clicked.
    launcherDisplayed: {
      name: 'AI Assistant: Launcher Displayed',
      properties: ['Variant'] as const,
    },
    opened: {
      name: 'AI Assistant: Opened',
      properties: ['Trigger Source'] as const,
    },
    closed: {
      name: 'AI Assistant: Closed',
      properties: [] as const,
    },
    transferredToTsChat: {
      name: 'AI Assistant: Transferred to TS Chat',
      properties: ['Message Count', 'Had Tool Calls'] as const,
    },
    transferredToTsSupportForm: {
      name: 'AI Assistant: Transferred to TS Support Form',
      properties: ['Message Count', 'Had Tool Calls'] as const,
    },
    transferredToAccountManagement: {
      name: 'AI Assistant: Transferred to Account Management',
      properties: ['Account Id', 'Is Premium', 'Selected Options', 'Message Count', 'Had Tool Calls'] as const,
    },
    suggestionClicked: {
      name: 'AI Assistant: Suggestion Clicked',
      properties: ['Index', 'Click Location', 'Conversation ID'] as const,
      // Index alone can't identify a suggestion — the pool is shuffled per render,
      // so position 1 is a different chip on every impression.
      // Suggestion Group aggregates chips that pitch one capability through
      // several rotating labels — without it, analysis has to union label strings.
      optionalProperties: ['Suggestion Label', 'Suggestion Group', 'Route Group', 'Suggestion Source'] as const,
    },
    promptCardClicked: {
      name: 'AI Assistant: Prompt Card Clicked',
      properties: ['Title', 'Source', 'Position'] as const,
    },
    newChatClicked: {
      name: 'AI Assistant: New Chat Clicked',
      properties: [] as const,
    },
    viewedHistory: {
      name: 'AI Assistant: Viewed History',
      properties: [] as const,
    },
    historyItemClicked: {
      name: 'AI Assistant: History Item Clicked',
      properties: [] as const,
    },
    historyItemRenamed: {
      name: 'AI Assistant: History Item Renamed',
      properties: [] as const,
    },
    historyItemDeleted: {
      name: 'AI Assistant: History Item Deleted',
      properties: [] as const,
    },
    feedbackLinkClicked: {
      name: 'AI Assistant: Feedback Link Clicked',
      properties: [] as const,
    },
    feedbackFormSubmitted: {
      name: 'AI Assistant: Feedback Form Submitted',
      properties: ['Feedback'] as const,
    },
    referencePaginated: {
      name: 'AI Assistant: Reference Paginated',
      properties: ['Direction', 'Current Index', 'Total References'] as const,
    },
    // Impression event — the denominator for suggestionClicked. Distinct from
    // `suggestionsShown`, which fires when the user un-hides the sidebar list.
    suggestionsDisplayed: {
      name: 'AI Assistant: Suggestions Displayed',
      properties: ['Click Location', 'Route Group', 'Suggestion Source', 'Suggestion Labels', 'Count'] as const,
    },
    suggestionsShown: {
      name: 'AI Assistant: Suggestions Shown',
      properties: [] as const,
    },
    suggestionsHidden: {
      name: 'AI Assistant: Suggestions Hidden',
      properties: [] as const,
    },
    editBusinessProfileClicked: {
      name: 'AI Assistant: Edit Business Profile Clicked',
      properties: ['Click Location'] as const,
    },
    contextUsedPopoverOpened: {
      name: 'AI Assistant: Context Used Popover Opened',
      properties: [] as const,
    },
    contextUsedAccordionClicked: {
      name: 'AI Assistant: Context Used Accordion Clicked',
      properties: ['section'] as const,
    },
    tourStarted: {
      name: 'AI Assistant: Tour Started',
      properties: ['Tour Name'] as const,
    },
    tourDismissed: {
      name: 'AI Assistant: Tour Dismissed',
      properties: ['Tour Name'] as const,
    },
    messageSent: {
      name: 'AI Assistant: Message Sent',
      properties: ['Message', 'Has Files', 'File Count', 'Turn Number', 'Is First Message', 'Conversation ID'] as const,
    },
    stopClicked: {
      name: 'AI Assistant: Stop Clicked',
      properties: [] as const,
    },
    messageCopied: {
      name: 'AI Assistant: Message Copied',
      properties: [] as const,
    },
    thumbsUp: {
      name: 'AI Assistant: Thumbs Up',
      properties: [] as const,
    },
    thumbsDown: {
      name: 'AI Assistant: Thumbs Down',
      properties: ['Feedback Text', 'Has Feedback'] as const,
    },
    fileUploaded: {
      name: 'AI Assistant: File Uploaded',
      properties: ['Count', 'File Types'] as const,
    },
    fileDownloaded: {
      name: 'AI Assistant: File Downloaded',
      properties: ['File Type'] as const,
    },
    expandedToFullScreen: {
      name: 'AI Assistant: Expanded to Full Screen',
      properties: [] as const,
    },
    collapsedFromFullScreen: {
      name: 'AI Assistant: Collapsed from Full Screen',
      properties: [] as const,
    },
    approvalModeChanged: {
      name: 'AI Assistant: Approval Mode Changed',
      properties: ['Mode'] as const,
    },
    webSearchToggled: {
      name: 'AI Assistant: Web Search Toggled',
      properties: ['Enabled'] as const,
    },
    routineSuggestionClicked: {
      name: 'AI Assistant: Routine Suggestion Clicked',
      properties: ['Title'] as const,
    },
    routineEditClicked: {
      name: 'AI Assistant: Routine Edit Clicked',
      properties: [] as const,
    },
    routineSaved: {
      name: 'AI Assistant: Routine Saved',
      properties: [] as const,
    },
    routinePaused: {
      name: 'AI Assistant: Routine Paused',
      properties: [] as const,
    },
    routinePausedFromEmail: {
      name: 'AI Assistant: Routine Paused From Email',
      properties: [] as const,
    },
    routineResumed: {
      name: 'AI Assistant: Routine Resumed',
      properties: [] as const,
    },
    routineDeleted: {
      name: 'AI Assistant: Routine Deleted',
      properties: [] as const,
    },
    skillCreated: {
      name: 'AI Assistant: Skill Created',
      properties: [] as const,
    },
    skillSaved: {
      name: 'AI Assistant: Skill Saved',
      properties: [] as const,
    },
    skillDeleted: {
      name: 'AI Assistant: Skill Deleted',
      properties: [] as const,
    },
    skillCreateWithAgentClicked: {
      name: 'AI Assistant: Skill Create With Agent Clicked',
      properties: [] as const,
    },
    skillWriteManuallyClicked: {
      name: 'AI Assistant: Skill Write Manually Clicked',
      properties: [] as const,
    },
    routineToolCreated: {
      name: 'AI Assistant: Routine Tool Created',
      properties: ['Action', 'Routine Name'] as const,
    },
    routineToolUpdated: {
      name: 'AI Assistant: Routine Tool Updated',
      properties: ['Action', 'Routine Name'] as const,
    },
    routineToolDeleted: {
      name: 'AI Assistant: Routine Tool Deleted',
      properties: ['Action', 'Routine Name'] as const,
    },
    routineToolRun: {
      name: 'AI Assistant: Routine Tool Run',
      properties: ['Action', 'Routine Name'] as const,
    },
    routineToolListed: {
      name: 'AI Assistant: Routine Tool Listed',
      properties: ['Action', 'Routine Name'] as const,
    },
    routineToolHistory: {
      name: 'AI Assistant: Routine Tool History',
      properties: ['Action', 'Routine Name'] as const,
    },
    tableExported: {
      name: 'AI Assistant: Table Exported',
      properties: [] as const,
    },
    tableCopied: {
      name: 'AI Assistant: Table Copied',
      properties: [] as const,
    },
    limitReached: {
      name: 'AI Assistant: Limit Reached',
      properties: ['Feature', 'Limit', 'Current Count'] as const,
    },
    capabilityApprovalConfirmed: {
      name: 'AI Assistant: Capability Approval Confirmed',
      properties: ['scopes'] as const,
    },
    capabilityApprovalDenied: {
      name: 'AI Assistant: Capability Approval Denied',
      properties: ['scopes'] as const,
    },
  },
  aiProfile: {
    viewed: {
      name: 'AI Profile: Viewed',
      properties: ['Fields Total', 'Fields AI Populated', 'Fields User Overridden', 'Fields Empty'] as const,
    },
    editIntent: {
      name: 'AI Profile: Edit Intent',
      properties: ['Field'] as const,
    },
    saved: {
      name: 'AI Profile: Saved',
      properties: [
        'Fields Changed Count',
        'Fields Added Count',
        'Fields Edited Count',
        'Fields Cleared Count',
        'Changed Fields',
        'Edit Session Ms',
      ] as const,
    },
    discarded: {
      name: 'AI Profile: Discarded',
      properties: ['Unsaved Fields Changed Count'] as const,
    },
    tonePreviewGetPreviewClicked: {
      name: 'AI Profile: Tone Preview Get Preview Clicked',
      properties: ['Channel', 'Sample Input Customized'] as const,
    },
    tonePreviewCopyOutputClicked: {
      name: 'AI Profile: Tone Preview Copy Output Clicked',
      properties: [] as const,
    },
    tonePreviewShuffleSampleInputClicked: {
      name: 'AI Profile: Tone Preview Shuffle Sample Input Clicked',
      properties: [] as const,
    },
    tonePreviewShowSampleInputToggled: {
      name: 'AI Profile: Tone Preview Show Sample Input Toggled',
      properties: [] as const,
    },
  },
  auditLogs: {
    viewed: {
      name: 'Audit Logs: Viewed',
      properties: ['contextType', 'hasFilters', 'workspaceId'] as const,
    },
    contextChanged: {
      name: 'Audit Logs: Context Changed',
      properties: ['contextType', 'autoSelected'] as const,
    },
    workspaceChanged: {
      name: 'Audit Logs: Workspace Changed',
      properties: ['workspaceId'] as const,
    },
    filtersChanged: {
      name: 'Audit Logs: Filters Changed',
      properties: ['filterCount'] as const,
    },
    exportCreated: {
      name: 'Audit Logs: Export Created',
      properties: ['context', 'contextId', 'startTime', 'endTime', 'objectType'] as const,
    },
    exportFailed: {
      name: 'Audit Logs: Export Failed',
      properties: ['context', 'contextId', 'error'] as const,
    },
  },
  deliverability: {
    viewed: {
      name: 'Deliverability: Viewed',
      properties: ['workspaceId', 'hasUnlimitedAccess'] as const,
    },
  },
  emailClients: {
    viewed: {
      name: 'Email Clients: Viewed',
      properties: ['workspaceId', 'hasUnlimitedAccess'] as const,
    },
    chartMetricChanged: {
      name: 'Email Clients: Chart Metric Changed',
      properties: ['metric'] as const,
    },
  },
  darkMode: {
    colorSchemeChanged: {
      name: 'Dark Mode: Color Scheme Changed',
      properties: ['Color Scheme', 'Previous Color Scheme'] as const,
    },
  },
  universalSearch: {
    opened: {
      name: 'Universal Search: Opened',
      properties: ['Entry Point'] as const,
    },
    itemSelected: {
      name: 'Universal Search: Item Selected',
      properties: [
        'Item Type',
        'Result Kind',
        'Query',
        'Result Position',
        'Results Count',
        'API Results Count',
        'Open in New Tab',
        'Time to First Result Ms',
        'Active Filter',
        'Active Sub Filter',
        'Filters Open',
        'Preview Open',
      ] as const,
    },
    previewOpened: {
      name: 'Universal Search: Preview Opened',
      properties: ['Result Kind', 'Query'] as const,
    },
    previewClosed: {
      name: 'Universal Search: Preview Closed',
      properties: ['Result Kind', 'Query'] as const,
    },
    filterChanged: {
      name: 'Universal Search: Filter Changed',
      properties: ['Filter Key', 'Filter Value', 'Previous Filter', 'Previous Sub Filter', 'Query'] as const,
    },
    filterVisibilityToggled: {
      name: 'Universal Search: Filter Visibility Toggled',
      properties: ['Visible'] as const,
    },
    closed: {
      name: 'Universal Search: Closed',
      properties: ['Reason', 'Had Query', 'Query', 'Time Open Ms', 'Active Filter', 'Active Sub Filter'] as const,
    },
  },
  customer_io_basics: {
    modal_viewed: {
      name: 'Customer.io Basics: Modal Viewed',
      properties: [] as const,
    },
    video_clicked: {
      name: 'Customer.io Basics: Video Clicked',
      properties: ['id'] as const,
    },
    video_started: {
      name: 'Customer.io Basics: Video Started',
      properties: ['id', 'name', 'watched', 'started', 'total'] as const,
    },
    video_watched: {
      name: 'Customer.io Basics: Video Watched',
      properties: ['id', 'name', 'watched', 'started', 'total'] as const,
    },
    video_stopped: {
      name: 'Customer.io Basics: Video Stopped',
      properties: ['id', 'name', 'pauseTime', 'watched', 'started', 'total'] as const,
    },
    video_time_shifted: {
      name: 'Customer.io Basics: Video Time Shifted',
      properties: ['id', 'name', 'seekTo', 'seekFrom'] as const,
    },
    previous_clicked: {
      name: 'Customer.io Basics: Previous Clicked',
      properties: ['id'] as const,
    },
    next_clicked: {
      name: 'Customer.io Basics: Next Clicked',
      properties: ['id'] as const,
    },
  },
  help_center: {
    opened: {
      name: 'Help Center: Help Menu Opened',
      properties: [] as const,
    },
    learnAcademyClicked: {
      name: 'Help Center: Learn with Academy',
      properties: [] as const,
    },
    askCommunityClicked: {
      name: 'Help Center: Ask the Community',
      properties: [] as const,
    },
    platformDocsClicked: {
      name: 'Help Center: Platform Documentation',
      properties: [] as const,
    },
    apiDocsClicked: {
      name: 'Help Center: API Documentation',
      properties: [] as const,
    },
    releaseNotesClicked: {
      name: 'Help Center: Release Notes',
      properties: [] as const,
    },
    connectWithPartnerClicked: {
      name: 'Help Center: Connect with a Partner',
      properties: [] as const,
    },
    feedbackRequestStarted: {
      name: 'Help Center: Send Us Feedback',
      properties: [] as const,
    },
    helpRequestStarted: {
      name: 'Help Center: Get Help With an Issue',
      properties: [] as const,
    },
    aiAssistantClicked: {
      name: 'Help Center: Chat with AI Assistant',
      properties: [] as const,
    },
    chatWithSupportClicked: {
      name: 'Help Center: Chat with Support',
      properties: [] as const,
    },
    systemStatusClicked: {
      name: 'Help Center: System Status',
      properties: [] as const,
    },
    grantSupportAccessClicked: {
      name: 'Help Center: Set Support Access',
      properties: [] as const,
    },
  },
  inline_support: {
    opened: {
      name: 'Inline Support: Opened',
      properties: ['Account Id', 'Requester Name', 'Requester Email', 'Requester User Id'] as const,
    },
    closed: {
      name: 'Inline Support: Closed',
      properties: ['Account Id', 'Requester Name', 'Requester Email', 'Requester User Id'] as const,
    },
    supportAccessGranted: {
      name: 'Inline Support: Support Access Granted',
      properties: ['Account Id', 'Requester Name', 'Requester Email', 'Requester User Id'] as const,
    },
    supportAccessError: {
      name: 'Inline Support: Support Access Error',
      properties: ['Account Id', 'Requester Name', 'Requester Email', 'Requester User Id'] as const,
    },
    submitClicked: {
      name: 'Inline Support: Submit Clicked',
      properties: ['Account Id', 'Requester Name', 'Requester Email', 'Requester User Id'] as const,
    },
    submitError: {
      name: 'Inline Support: Submit Error',
      properties: ['Account Id', 'Requester Name', 'Requester Email', 'Requester User Id'] as const,
    },
    submitSuccess: {
      name: 'Inline Support: Submit Success',
      properties: ['Account Id', 'Requester Name', 'Requester Email', 'Requester User Id'] as const,
    },
    supportAccessGuidanceShown: {
      name: 'Inline Support: Support Access Guidance Shown',
      properties: ['Account Id', 'Requester Name', 'Requester Email', 'Requester User Id'] as const,
    },
  },
  plan_and_billing: {
    supportRequestStarted: {
      name: 'Plan and Billing: Support Request Started',
      properties: ['accountId', 'workspaceId', 'source'] as const,
    },
    supportRequestCompleted: {
      name: 'Plan and Billing: Support Request Completed',
      properties: ['accountId', 'workspaceId', 'comment', 'source'] as const,
    },
  },
  mainNav: {
    toggleCollapsed: {
      name: 'main-nav',
      properties: ['isCollapsed'] as const,
    },
  },
  leftNav: {
    itemClicked: {
      name: 'Left Nav: Item Clicked',
      properties: ['Nav Item', 'Nav Section', 'Is Nested', 'Parent Item', 'Entry Point'] as const,
    },
    customizeModalOpened: {
      name: 'Left Nav: Customize Modal Opened',
      properties: [] as const,
    },
    preferenceChanged: {
      name: 'Left Nav: Preference Changed',
      properties: ['Item ID', 'Section', 'Previous Value', 'New Value', 'Source'] as const,
    },
    moreMenuItemClicked: {
      name: 'Left Nav: More Menu Item Clicked',
      properties: ['Item ID', 'Section', 'URL'] as const,
    },
    collapsed: {
      name: 'Left Nav: Collapsed',
      properties: ['Trigger', 'Session Nav Opens After Collapse'] as const,
    },
    expanded: {
      name: 'Left Nav: Expanded',
      properties: ['Trigger', 'Time Collapsed Ms'] as const,
    },
  },
  performance: {
    hydraInitialLoad: {
      name: 'Performance: Hydra Initial Load',
      properties: [
        'Account Id',
        'Data Center',
        'Route Name',
        'Has Tried To Override Metric',
        'Hydra Boot Start',
        'Hydra Authenticated',
        'Hydra Child App Load Start',
        'Hydra Time To Authenticated',
        'Hydra Time To Child App Load Start',
      ] as const,
    },
  },
  workspaceHealth: {
    tabChanged: {
      name: 'Workspace Performance: Tab Changed',
      properties: ['Tab'] as const,
    },
    viewAllClicked: {
      name: 'Workspace Performance: View All Clicked',
      properties: ['Destination'] as const,
    },
    drawerOpened: {
      name: 'Workspace Performance: Drawer Opened',
      properties: ['Drawer'] as const,
    },
    viewBreakdownClicked: {
      name: 'Workspace Performance: View Breakdown Clicked',
      properties: [] as const,
    },
    recommendationCarouselClicked: {
      name: 'Workspace Performance: Recommendation Carousel Clicked',
      properties: ['Direction'] as const,
    },
    recommendationClicked: {
      name: 'Workspace Performance: Recommendation Clicked',
      properties: ['Recommendation'] as const,
    },
    notificationClicked: {
      name: 'Workspace Performance: Notification Clicked',
      properties: [] as const,
    },
    notificationLinkClicked: {
      name: 'Workspace Performance: Notification Link Clicked',
      properties: [] as const,
    },
    askAgentClicked: {
      name: 'Workspace Performance: Ask Agent Clicked',
      properties: ['Panel'] as const,
      // Set only when the prompt came from a specific alert's own `copilot_prompt`; absent for the
      // workspace-wide prompt.
      optionalProperties: ['Alert'] as const,
    },
  },
  tasks: {
    paginated: {
      name: 'Tasks: Paginated',
      properties: ['Direction'] as const,
    },
    paginationReset: {
      name: 'Tasks: Pagination Reset',
      properties: [] as const,
    },
    viewChanged: {
      name: 'Tasks: View Changed',
      properties: ['View'] as const,
    },
    filterApplied: {
      name: 'Tasks: Filter Applied',
      properties: ['Filter Key', 'Filter Value'] as const,
    },
    priorityChanged: {
      name: 'Tasks: Priority Changed',
      properties: ['Mode', 'Task Type'] as const,
    },
    queueDragMoved: {
      name: 'Tasks: Queue Drag Moved',
      properties: ['Task Type', 'Queue', 'Position'] as const,
    },
    canceled: {
      name: 'Tasks: Canceled',
      properties: ['Task Type'] as const,
    },
  },
  // The post-signup onboarding wizard at /onboarding/wizard (Self Serve Relaunch).
  // Fired through the account-scoped tracker: the page has no workspace yet.
  // Downstream dashboards key on these exact names — keep them byte-identical.
  onboardingWizard: {
    // `Screen` is `chat` for the conversation; the resume properties ride on it.
    // Older events also carry `verification`, from the retired
    // /onboarding/verification hold.
    viewed: {
      name: 'Onboarding Wizard: Viewed',
      properties: ['Screen'] as const,
      optionalProperties: ['Resume Step', 'Answered Count', 'Is First Run'] as const,
    },
    questionViewed: {
      name: 'Onboarding Wizard: Question Viewed',
      properties: ['Question', 'Question Index'] as const,
    },
    // `Answer` is the chip id, the goal label or typed text, or the channel id
    // list; `Answer Type` is `preset` for a chip and `custom` for a typed goal.
    // The last question (`channels`) also carries every stored answer: that
    // row is the wizard's completion, there is no separate event for it.
    questionAnswered: {
      name: 'Onboarding Wizard: Question Answered',
      properties: ['Question', 'Question Index', 'Answer', 'Answer Type'] as const,
      optionalProperties: [
        'Answer Count',
        'Goal',
        'Goal Type',
        'Starting Point',
        'Platform',
        'Channels',
        'Is Handing Off',
      ] as const,
    },
    answerSaveFailed: {
      name: 'Onboarding Wizard: Answer Save Failed',
      properties: ['Question', 'Reason'] as const,
      optionalProperties: ['Status'] as const,
    },
    exited: {
      name: 'Onboarding Wizard: Exited',
      properties: ['Reason', 'Destination', 'Was Complete On Mount'] as const,
    },
  },
  // Mixpanel Experiments' exposure event, sent by `useExperiment` in both arms.
  // The tracking endpoint drops names containing `$` or `_`, so this stands in
  // for Mixpanel's default `$experiment_started`; the project maps it by name.
  experiment: {
    started: {
      name: 'Experiment Started',
      properties: ['Experiment name', 'Variant name'] as const,
    },
    // Throwaway: the test button's conversion, so the example experiment has
    // a metric only treatment can move. Delete with the button.
    testButtonClicked: {
      name: 'Experiment Test: Button Clicked',
      properties: ['Experiment name', 'Variant name'] as const,
    },
  },
  // The essentials onboarding checklist on home (Self Serve Relaunch), fired
  // through the workspace-scoped tracker. The screen's own view rides on
  // `home.viewed` with `is_onboarding: true` rather than a separate event.
  // Downstream dashboards key on these exact names — keep them byte-identical.
  onboardingChecklist: {
    // `Source` is `initial` for the step on stage at load, `rail` for a rail
    // click, `skip` for the move a skip makes, `next` for the Next button on a
    // finished step, `auto` for anything else (a completion or clamp moving the
    // stage).
    stepViewed: {
      name: 'Onboarding Checklist: Step Viewed',
      properties: [
        'Step',
        'Step Index',
        'Starting Point',
        'Is Complete',
        'Is Skipped',
        'Is Blocked',
        'Source',
      ] as const,
      optionalProperties: ['From Step', 'Is MCP Fully Enabled'] as const,
    },
    stepSkipped: {
      name: 'Onboarding Checklist: Step Skipped',
      properties: ['Step', 'Step Index', 'Starting Point'] as const,
    },
    // A step observed incomplete flipping to complete while the screen is
    // mounted; deduped per workspace and step, never for a step already
    // complete on first render.
    stepCompleted: {
      name: 'Onboarding Checklist: Step Completed',
      properties: ['Step', 'Step Index', 'Starting Point', 'Was Skipped'] as const,
    },
    // One event for every call to action, told apart by `CTA`
    // (`data_source` | `starting_point` | `templates` | `mcp_client` | `mcp_manual_setup` | `invite_teammate` |
    // `go_live` | `sending_domain` | `verify_account`);
    // the optional properties belong to particular CTAs.
    ctaClicked: {
      name: 'Onboarding Checklist: CTA Clicked',
      properties: ['CTA', 'Step', 'Starting Point'] as const,
      optionalProperties: [
        'Data Source',
        'Listen Targets',
        'Client',
        'Is MCP Fully Enabled',
        'Completed Steps',
        'Completed Count',
        'Skipped Steps',
        'Domain Action',
      ] as const,
    },
    inviteModalClosed: {
      name: 'Onboarding Checklist: Invite Modal Closed',
      properties: ['Step', 'Starting Point', 'Invites Sent'] as const,
    },
    verificationViewed: {
      name: 'Onboarding Checklist: Verification Viewed',
      properties: ['Phase', 'Starting Point'] as const,
      optionalProperties: ['From Step'] as const,
    },
    wentLive: {
      name: 'Onboarding Checklist: Went Live',
      properties: ['Completed Steps', 'Completed Count', 'Skipped Steps', 'Starting Point'] as const,
    },
    // `Action` is `go_live` | `invite`. `Message` is the user-facing text for
    // go_live and a normalized reason for invite, whose server detail can carry
    // the invitee's email.
    actionFailed: {
      name: 'Onboarding Checklist: Action Failed',
      properties: ['Action', 'Message'] as const,
    },
    agentHandoffStoodDown: {
      name: 'Onboarding Checklist: Agent Handoff Stood Down',
      properties: ['Reason', 'Active Step'] as const,
    },
    returnHintViewed: {
      name: 'Onboarding Checklist: Return Hint Viewed',
      properties: [] as const,
    },
    returnHintDismissed: {
      name: 'Onboarding Checklist: Return Hint Dismissed',
      properties: ['Reason'] as const,
    },
  },
  home: {
    // The onboarding checklist reports its own view with `is_onboarding: true`
    // and the checklist properties; every other screen sends the two flags.
    viewed: {
      name: 'Home: Viewed',
      properties: ['is_builder', 'is_onboarding'] as const,
      optionalProperties: [
        'Starting Point',
        'Active Step',
        'Completed Steps',
        'Completed Count',
        'Skipped Steps',
        'Is Account Verified',
        'Has Agent Handoff Intent',
      ] as const,
    },
    recentCardClicked: {
      name: 'Home: Recent Card Clicked',
      properties: ['Resource Type', 'Position', 'Is Create Action', 'is_onboarding'] as const,
    },
    recentActivityClicked: {
      name: 'Home: Recent Activity Clicked',
      properties: ['Resource Type', 'Action'] as const,
    },
    addFundsClicked: {
      name: 'Home: Clicked Add Funds CTA',
      properties: [] as const,
    },
    trialUpgradeClicked: {
      name: 'Home: Trial Progress Upgrade Clicked',
      properties: ['Account Id'] as const,
    },
    inviteTeammateClicked: {
      name: 'Home: Invite Teammate Clicked',
      properties: [] as const,
    },
    messageInvadersOpened: {
      name: 'Home: Message Invaders Opened',
      properties: [] as const,
    },
    messageInvadersGameOver: {
      name: 'Home: Message Invaders Game Over',
      properties: ['Score', 'Wave', 'Is New High Score'] as const,
    },
    flappyMessageOpened: {
      name: 'Home: Flappy Message Opened',
      properties: [] as const,
    },
    flappyMessageGameOver: {
      name: 'Home: Flappy Message Game Over',
      properties: ['Score', 'Is New High Score'] as const,
    },
    agentInputSubmitted: {
      name: 'Home: Agent Input Submitted',
      properties: [] as const,
    },
    workspaceActivityGraphEdited: {
      name: 'Home: Workspace Activity Graph Edited',
      properties: ['Channel'] as const,
    },
    clickedAnalysisLink: {
      name: 'Home: Clicked Analysis Link',
      properties: [] as const,
    },
    clickedClassicDashboard: {
      name: 'Home: Clicked Classic Dashboard',
      properties: [] as const,
    },
    welcomeBannerDismissed: {
      name: 'Home: Welcome Banner Dismissed',
      properties: [] as const,
    },
    welcomeBannerCtaClicked: {
      name: 'Home: Welcome Banner CTA Clicked',
      properties: [] as const,
    },
    promptCardsShown: {
      name: 'Home: Prompt Cards Shown',
      properties: ['Titles', 'Personalized'] as const,
    },
    auditLogsClicked: {
      name: 'Home: Audit Logs Clicked',
      properties: [] as const,
    },
  },
  builders: {
    addFundsClicked: {
      name: 'Builders: Add Funds Clicked',
      properties: [...BuildersGlobalProperties, 'page_name'] as const,
    },
    apiKeySaved: {
      name: 'Builders: API Key Saved',
      properties: [...BuildersGlobalProperties, 'page_name', 'service_account_id'] as const,
    },
    domainAdded: {
      name: 'Builders: Domain Added',
      properties: [...BuildersGlobalProperties, 'page_name', 'domain', 'domain_id'] as const,
    },
    domainVerified: {
      name: 'Builders: Domain Verified',
      properties: [...BuildersGlobalProperties, 'domain', 'verification_method'] as const,
    },
    userAdded: {
      name: 'Builders: User Added',
      properties: [
        ...BuildersGlobalProperties,
        'page_name',
        'is_test_user',
        'user_count',
        'method',
        'remaining_spots',
        'has_email',
      ] as const,
    },
    messageSent: {
      name: 'Builders: Message Sent',
      properties: [...BuildersGlobalProperties, 'is_test_send', 'channel', 'message_type'] as const,
    },
    upNextClicked: {
      name: 'Builders: Up Next Clicked',
      properties: [...BuildersGlobalProperties, 'page_name', 'step'] as const,
    },
    stepSkipped: {
      name: 'Builders: Step Skipped',
      properties: [...BuildersGlobalProperties, 'page_name', 'step'] as const,
    },
    sectionViewed: {
      name: 'Builders: Onboarding Section Viewed',
      properties: [...BuildersGlobalProperties, 'page_name', 'section'] as const,
    },
    codeSnippetCopied: {
      name: 'Builders: Onboarding Code Snippet Copied',
      properties: [...BuildersGlobalProperties, 'page_name', 'language', 'section'] as const,
    },
    emailTemplateClicked: {
      name: 'Builders: Onboarding Email Template Clicked',
      properties: [...BuildersGlobalProperties, 'page_name', 'email_name'] as const,
    },
    headerCtaClicked: {
      name: 'Builders: Onboarding Header CTA Clicked',
      properties: [...BuildersGlobalProperties, 'page_name'] as const,
    },
    productionKeyGenerated: {
      name: 'Builders: Onboarding Production Key Generated',
      properties: [...BuildersGlobalProperties, 'page_name'] as const,
    },
    keysSwappedConfirmed: {
      name: 'Builders: Onboarding Keys Swapped Confirmed',
      properties: [...BuildersGlobalProperties, 'page_name'] as const,
    },
  },
  // Event names intentionally match the existing credits-purchase events fired
  // from the My Plan settings surface so both report into the same Mixpanel
  // events; page_name distinguishes the source. `optionalProperties` documents
  // the source-varying keys the Your Plan flow sends (billing system, payment
  // method, save-card, etc.) that the Builder sandbox purchase surface omits —
  // `track` accepts them as extra keys beyond the required set.
  creditsPurchase: {
    modalOpened: {
      name: 'Credits Purchase: Modal Opened',
      properties: ['Credit Type', 'Account Id', 'page_name'] as const,
      optionalProperties: ['Current Plan', 'Billing System'] as const,
    },
    modalClosed: {
      name: 'Credits Purchase: Modal Closed',
      properties: ['Credit Type', 'page_name'] as const,
      optionalProperties: ['Had Interaction', 'Billing System'] as const,
    },
    quantityChanged: {
      name: 'Credits Purchase: Quantity Changed',
      properties: ['Credit Type', 'Quantity', 'Total Price Cents', 'page_name'] as const,
      optionalProperties: ['Billing System'] as const,
    },
    paymentMethodChanged: {
      name: 'Credits Purchase: Payment Method Changed',
      properties: ['Credit Type', 'Payment Method', 'page_name'] as const,
    },
    purchaseSubmitted: {
      name: 'Credits Purchase: Purchase Submitted',
      properties: [
        'Credit Type',
        'Account Id',
        'Quantity',
        'Total Price Cents',
        'Tax Amount Cents',
        'Total With Tax Cents',
        'page_name',
      ] as const,
      optionalProperties: ['Current Plan', 'Payment Method', 'Save Card', 'Total Credits', 'Billing System'] as const,
    },
    purchaseSucceeded: {
      name: 'Credits Purchase: Purchase Succeeded',
      properties: [
        'Credit Type',
        'Account Id',
        'Quantity',
        'Total Price Cents',
        'Tax Amount Cents',
        'Total With Tax Cents',
        'page_name',
      ] as const,
      optionalProperties: ['Total Credits', 'Billing System'] as const,
    },
    purchaseFailed: {
      name: 'Credits Purchase: Purchase Failed',
      properties: ['Credit Type', 'Account Id', 'Quantity', 'Total Price Cents', 'Error Type', 'page_name'] as const,
      optionalProperties: ['Billing System'] as const,
    },
  },
  // Event names and property shapes match the Ember sending-domain settings
  // flow (ui repo, app/components/entri-dns-setup) so both surfaces report into
  // the same Mixpanel events. Fired from the shared Entri setup hooks, so they
  // cover both the legacy builders-setup and the sandbox onboarding domain steps.
  email: {
    // `Trigger` separates a setup the customer clicked into from one launched
    // for them after they added a domain. Only the sending-domain screens know
    // which it was, so the onboarding screens send the event without it.
    domainSetupStarted: {
      name: 'Email: Deliverability Domain Setup Started',
      properties: ['Domain', 'Setup Type'] as const,
      optionalProperties: ['Trigger'] as const,
    },
    // Ember fired this per transition into `actions.email.sending_domains.index`
    // via its route→event map; the migrated tab fires it on page view instead.
    sendingDomainsTabViewed: {
      name: 'Email: Sending Domains Tab Viewed',
      properties: [] as const,
    },
    domainAdded: {
      name: 'Email: Domain Added',
      properties: ['Domain'] as const,
    },
    // The one "viewed" event the sending-domain detail screen reports, fired
    // once per visit regardless of how many panels the screen shows.
    deliverabilityDomainViewed: {
      name: 'Email: Deliverability Domain Viewed',
      properties: ['Domain'] as const,
    },
    deliverabilityDomainCheckingStarted: {
      name: 'Email: Deliverability Domain Checking Started',
      properties: ['Domain'] as const,
    },
    linkTrackingDomainCheckingStarted: {
      name: 'Email: Link Tracking Domain Checking Started',
      properties: ['Domain'] as const,
    },
    deliverabilityDomainDnsCheckStarted: {
      name: 'Email: Deliverability Domain DNS Check Started',
      properties: ['Domain'] as const,
    },
    domainVerified: {
      name: 'Email: Domain Verified',
      properties: ['Domain'] as const,
    },
    domainDnsReconfigureStarted: {
      name: 'Email: Domain DNS Reconfigure Started',
      properties: ['Domain'] as const,
    },
    domainDnsReconfigureCanceled: {
      name: 'Email: Domain DNS Reconfigure Canceled',
      properties: ['Domain'] as const,
    },
    manualAuthentication: {
      name: 'Email: Chose manual authentication',
      properties: ['Domain'] as const,
    },
    domainSetupClicked: {
      name: 'Email: Clicks Domain Setup',
      properties: ['Domain', 'Setup Type', 'Trigger'] as const,
    },
    linkTrackingSetupClicked: {
      name: 'Email: Link Tracking Setup Clicked',
      properties: ['Domain', 'Setup Type', 'Trigger'] as const,
    },
    linkTrackingSetupStarted: {
      name: 'Email: Link Tracking Setup Started',
      properties: ['Domain', 'Setup Type', 'Trigger'] as const,
    },
    linkTrackingSetupCompleted: {
      name: 'Email: Link Tracking Setup Completed',
      properties: ['Domain', 'Provider', 'Trigger'] as const,
    },
    linkTrackingSetupAbandoned: {
      name: 'Email: Link Tracking Setup Abandoned',
      properties: ['Domain', 'Provider', 'Last Status', 'Setup Type', 'Trigger'] as const,
    },
    // Fired when the customer stops waiting for Entri-published records to
    // propagate and sets them up by hand. `Stalled` separates "it visibly wasn't
    // working" from "they didn't want to wait".
    entriPropagationDismissed: {
      name: 'Email: Entri Propagation Dismissed',
      properties: ['Domain', 'Flow', 'Stalled'] as const,
    },
    domainSetupCompleted: {
      name: 'Email: Deliverability Domain Setup Completed',
      properties: ['Domain', 'Provider'] as const,
      optionalProperties: ['Trigger'] as const,
    },
    // The Custom SMTP tab. Ember fired this per transition into
    // `actions.email.smtp`; the migrated tab fires it on page view instead.
    smtpTabViewed: {
      name: 'Email: Custom SMTP Settings Tab Viewed',
      properties: [] as const,
    },
    // The Suppression list tab. Ember's route→event map has no exact-match entry
    // for this route, so a query-param change (search, page, category) counts as
    // another view; the migrated tab keys its page view the same way.
    suppressionsTabViewed: {
      name: 'Email: Suppressions List Tab Viewed',
      properties: [] as const,
    },
    // The Default email settings tab. Ember fired these three from the component
    // rather than its route→event map.
    defaultSettingsTabViewed: {
      name: 'Email: Default Settings Tab Viewed',
      properties: [] as const,
    },
    defaultSettingToggled: {
      name: 'Email: Default Setting Toggled',
      properties: ['Setting', 'Toggled On'] as const,
    },
    // `Value` is the chosen option's label, not its id — the two sentinel
    // options have no address to report.
    defaultFromIdentityChanged: {
      name: 'Email: Default From Identity Changed',
      properties: ['Setting', 'Value'] as const,
    },
    // The add-server funnel. `Custom SMTP Server` is the provider's display
    // name, empty until one has been picked.
    customSmtpAddServerStarted: {
      name: 'Custom SMTP Add Server Started',
      properties: [] as const,
    },
    customSmtpAddServerSetupActionComplete: {
      name: 'Custom SMTP Add Server Setup Action Complete',
      properties: ['Custom SMTP Setup Action', 'Custom SMTP Server'] as const,
    },
    customSmtpAddServerSetupCanceled: {
      name: 'Custom SMTP Add Server Setup Canceled',
      properties: ['Custom SMTP Setup Step', 'Custom SMTP Server'] as const,
    },
    domainSetupAbandoned: {
      name: 'Email: Deliverability Domain Setup Abandoned',
      properties: ['Domain', 'Provider', 'Last Status', 'Setup Type'] as const,
      optionalProperties: ['Trigger'] as const,
    },
    // Design Studio dashboard-port event (CON-7496) — parcel's
    // `email.created`, fired by the file-upload flow.
    // `source` is how the email was made (scratch / template / import /
    // duplicated); `Surface` is where it was made from, always 'UI' here.
    // ui_api reports API / MCP / CLI / AI Assistant creations itself, and
    // also reports every `bulk_duplicate` copy (browser included), so the
    // dashboard doesn't fire this event for that endpoint (CON-8352).
    created: {
      name: 'Design Studio: Email Created',
      properties: [
        'id',
        'path',
        'source',
        'sourceEmailId',
        'editorType',
        'isTemplate',
        'startFrom',
        'Surface',
      ] as const,
    },
    // Parcel's `email.deleted` — parcel fires it from its Overmind
    // `deleteNode` action (editor file tree); the dashboard's copy fires from
    // the node context menu (CON-7986).
    deleted: {
      name: 'Design Studio: Email Deleted',
      properties: ['id', 'path'] as const,
    },
  },
  workspaceSetup: {
    clickedChannel: {
      name: 'Workspace Setup: Clicked channel',
      properties: ['account_id', 'channel'] as const,
    },
    clickedDataImport: {
      name: 'Workspace Setup: Clicked data import',
      properties: ['account_id'] as const,
    },
    recommendedNextStep: {
      name: 'Workspace Setup: Clicked recommended next step',
      properties: ['account_id', 'step'] as const,
    },
    viewedAddPeople: {
      name: 'Workspace Setup: Viewed add people',
      properties: [] as const,
    },
    clickedAddPeopleAction: {
      name: 'Workspace Setup: Clicked add people action',
      properties: ['account_id', 'actionName'] as const,
    },
    clickedAddAndVerifyDomain: {
      name: 'Workspace Setup: Clicked add and verify sending domain',
      properties: ['account_id', 'source'] as const,
    },
    clickedAddFromAddress: {
      name: 'Workspace Setup: Clicked add a From address',
      properties: ['account_id', 'source'] as const,
    },
    clickedAddFunds: {
      name: 'Workspace Setup: Clicked add funds',
      properties: ['account_id', 'source'] as const,
    },
  },
  test_delivery_mode: {
    popover_opened: {
      name: 'Test Delivery Mode: Popover Opened',
      properties: ['Route Name'],
    },
    popover_cta_clicked: {
      name: 'Test Delivery Mode: Popover CTA Clicked',
      properties: ['Route Name', 'Action Name'],
    },
  },
  experimentalFeatures: {
    pageViewed: {
      name: 'Experimental Features: Page Viewed',
      properties: [] as const,
    },
    featureToggled: {
      name: 'Experimental Features: Feature Toggled',
      properties: ['Feature', 'Toggled On'] as const,
    },
  },
  apiKeys: {
    pageViewed: {
      name: 'API Keys: Page Viewed',
      properties: ['Tab', 'Context'] as const,
    },
    serviceAccountCreated: {
      name: 'API Keys: Service Account Created',
      properties: [] as const,
    },
    serviceAccountDeleted: {
      name: 'API Keys: Service Account Deleted',
      properties: [] as const,
    },
    pipelineKeyCreated: {
      name: 'API Keys: Pipeline Key Created',
      properties: ['Source Name'] as const,
    },
    pipelineKeyDeleted: {
      name: 'API Keys: Pipeline Key Deleted',
      properties: ['Source Name'] as const,
    },
    signingKeyRegenerated: {
      name: 'API Keys: Signing Key Regenerated',
      properties: [] as const,
    },
  },
  // Data-warehouse sync (FPS-2317). Wire names and payload keys mirror the Ember
  // events so the existing dashboards keep resolving. `Type` is the destination's
  // display name, not its id — that is what Ember sent.
  sqlImports: {
    /** The create wizard was opened; `Database Type` is the engine's product name. */
    createStarted: {
      name: 'SQL Sync: Create Started',
      properties: ['Database Type'] as const,
    },
    /** The Details step was completed. */
    settingsStarted: {
      name: 'SQL Sync: Settings Started',
      properties: ['Database Type'] as const,
    },
    /** The Settings step was completed; `Interval` counts `Frequency` units. */
    selectDatabaseStarted: {
      name: 'SQL Sync: Select Database Started',
      properties: ['Frequency', 'Interval', 'Sync To Segment', 'Sync Target'] as const,
    },
    /** The Review step was shown. */
    reviewImport: {
      name: 'SQL Sync: Review Import',
      properties: ['query', 'Sync Target'] as const,
      optionalProperties: ['Relationship Query'] as const,
    },
    /** The wizard's final save succeeded. */
    createSyncStarted: {
      name: 'SQL Sync: Create Sync Started',
      properties: ['Database Type', 'Sync Target'] as const,
    },
    /** The Database step opened the form for a new database. */
    addDatabaseStarted: {
      name: 'SQL Sync: Add Database Started',
      properties: ['Database Type', 'Sync Target'] as const,
    },
    /** A new database failed to save; `Error Message` is the backend's detail. */
    addDatabaseFailed: {
      name: 'SQL Sync: Add Database Failed',
      properties: ['Database Type', 'Sync Target', 'Error Message'] as const,
    },
    /** The Database step opened the form over a saved database. */
    editDatabaseStarted: {
      name: 'SQL Sync: Edit Database Started',
      properties: ['Database Type', 'Sync Target'] as const,
    },
    /** A saved database failed to update; `Error Message` is the backend's detail. */
    editDatabaseFailed: {
      name: 'SQL Sync: Edit Database Failed',
      properties: ['Database Type', 'Sync Target', 'Error Message'] as const,
    },
    /** A database delete was confirmed. */
    deleteDatabaseStarted: {
      name: 'SQL Sync: Delete Database Started',
      properties: ['Database Type', 'Sync Target'] as const,
    },
    /** The Query step opened for a new import. */
    writeQueryStarted: {
      name: 'SQL Sync: Write Query Started',
      properties: ['Database Type'] as const,
    },
    /** The Relationships step opened for a new import. */
    writeRelationshipsQueryStarted: {
      name: 'SQL Sync: Write Relationships Query Started',
      properties: ['Database Type'] as const,
    },
    /** A preview was requested. */
    runQueryStarted: {
      name: 'SQL Sync: Run Query Started',
      properties: ['Database Type', 'Sync Target', 'Is Relationship Query'] as const,
    },
    /** A preview came back with an error or a blocking validation; `Error Message` is the backend's text. */
    runQueryFailed: {
      name: 'SQL Sync: Run Query Failed',
      properties: ['Database Type', 'Sync Target', 'Error Message'] as const,
    },
    /** The Relationships step was skipped. */
    skipRelationshipsQuery: {
      name: 'SQL Sync: Skip Relationships Query',
      properties: ['Database Type', 'Sync Target'] as const,
    },
    /** The preview timed out and the banner was dismissed to try again. */
    previewTimeoutDismiss: {
      name: 'SQL Sync: Preview Timeout Dismiss',
      properties: ['Database Type'] as const,
    },
    /** The preview timed out and the query was accepted without a preview. */
    previewTimeoutContinue: {
      name: 'SQL Sync: Preview Timeout Continue',
      properties: ['Database Type'] as const,
    },
    /** "Cancel import" was clicked on a running import. */
    cancelImport: {
      name: 'SQL Sync: Stop Import',
      properties: [] as const,
    },
    /** The cancel was confirmed and the run stopped. */
    cancelImportStarted: {
      name: 'SQL Sync: Stop Import Started',
      properties: [] as const,
    },
    /** The cancel confirmation was closed without stopping the run. */
    cancelImportCanceled: {
      name: 'SQL Sync: Stop Import Canceled',
      properties: [] as const,
    },
    /** A failed run's error file was downloaded. */
    downloadErrorFile: {
      name: 'SQL Sync: Download Error File',
      properties: [] as const,
    },
    syncDeleteStarted: {
      name: 'SQL Sync: Delete Started',
      properties: [] as const,
    },
    syncActivateStarted: {
      name: 'SQL Sync: Activate Started',
      properties: [] as const,
    },
    syncPauseStarted: {
      name: 'SQL Sync: Pause Started',
      properties: [] as const,
    },
    // The four tab views (FPS-4001). These names match the entries Ember fired before the
    // migration, so the existing lexicon series continue rather than new ones starting. The
    // Imports tab carries the sync's status where the others carry the sync target, which is
    // the split Ember had.
    /** The Imports tab was viewed; `Sync Status` is Active or Paused. */
    importsTabViewed: {
      name: 'SQL Sync: Sync Imports Viewed',
      properties: ['Database Type', 'Sync Status'] as const,
    },
    /** The Query tab was viewed. */
    queryTabViewed: {
      name: 'SQL Sync: Query Tab Viewed',
      properties: ['Database Type', 'Sync Target'] as const,
    },
    /** The Relationships query tab was viewed. */
    relationshipQueryTabViewed: {
      name: 'SQL Sync: Relationship Query Tab Viewed',
      properties: ['Database Type', 'Sync Target'] as const,
    },
    /** The Settings tab was viewed. */
    settingsTabViewed: {
      name: 'SQL Sync: Settings Tab Viewed',
      properties: ['Database Type', 'Sync Target'] as const,
    },
  },
  warehouse: {
    /** The connected config view. Ember fired this from its route-transition map. */
    settingsViewed: {
      name: 'Data Warehouse: Settings Viewed',
      properties: ['Type'] as const,
    },
    /** The same view before a sync exists — the blank slate is its own funnel step. */
    emptyStateViewed: {
      name: 'Data Warehouse: Empty State Viewed',
      properties: ['Type'] as const,
    },
    /** One of the three connector landing pages. */
    destinationViewed: {
      name: 'Data Warehouse: Destination Viewed',
      properties: ['Type'] as const,
    },
    exportEnabled: {
      name: 'Data Warehouse: Export Enabled',
      properties: ['Type', 'Export Type'] as const,
    },
    exportDisabled: {
      name: 'Data Warehouse: Export Disabled',
      properties: ['Type', 'Export Type'] as const,
    },
    disconnectSyncCompleted: {
      name: 'Data Warehouse: Disconnect Sync Completed',
      properties: ['Type'] as const,
    },
    updateConfigurationStarted: {
      name: 'Data Warehouse: Update Configuration Started',
      properties: ['Type'] as const,
    },
    updateConfigurationCompleted: {
      name: 'Data Warehouse: Update Configuration Completed',
      properties: ['Type'] as const,
    },
    createSyncStarted: {
      name: 'Data Warehouse: Create Sync Started',
      properties: ['Type'] as const,
    },
    createSyncCompleted: {
      name: 'Data Warehouse: Create Sync Completed',
      properties: ['Type'] as const,
    },
  },
  // LINE channel settings. Names and properties match the Ember catalog
  // (`ui/app/utils/analytics/events.ts`).
  line: {
    // Fired per resolved load of the settings page — the first one, and each
    // reload after the API key is saved or the channel is disabled.
    settingsViewed: {
      name: 'Line: Message Settings Viewed',
      properties: ['enabled'] as const,
    },
  },
  // Twilio channel settings. Name and property match the Ember catalog
  // (`ui/app/utils/analytics/events.ts` + `transforms/integrations.ts`).
  twilio: {
    // Fired per resolved load of the settings page — the first one, and each
    // reload after the credential is saved or removed.
    settingsViewed: {
      name: 'Twilio: Message Settings Viewed',
      properties: ['SMS Status'] as const,
    },
  },
  // Slack channel settings. Names and properties match the Ember catalog
  // (`ui/app/utils/analytics/events.ts` + `transforms/integrations.ts`).
  slack: {
    settingsViewed: {
      name: 'Slack: Message Settings Viewed',
      properties: ['Slack Status'] as const,
    },
    // `Source` names the screen the enable started from. The setup guide sends
    // "Onboarding"; the settings page is the channel's own screen and sends none.
    enablingStarted: {
      name: 'Slack: Enabling Started',
      properties: [] as const,
      optionalProperties: ['Source'] as const,
    },
  },
  // In-app channel settings. `Setup Progress` is how far the workspace has got
  // (connected its app, delivered a test); it is null whenever in-app is not
  // enabled, and on the disabled event.
  inApp: {
    settingsViewed: {
      name: 'In-App Message: Settings Viewed',
      properties: ['Gist In-App Messaging Status', 'Setup Progress'] as const,
    },
    enablingStarted: {
      name: 'In-App Message: Enabling Started',
      properties: ['Gist In-App Messaging Status', 'Setup Progress'] as const,
    },
    enablingCompleted: {
      name: 'In-App Message: Enabling Completed',
      properties: ['Gist In-App Messaging Status', 'Setup Progress'] as const,
    },
    disabled: {
      name: 'In-App Message: Disabled',
      properties: ['Gist In-App Messaging Status', 'Setup Progress'] as const,
    },
    inviteClicked: {
      name: 'In-App: Invite Team Member Clicked',
      properties: [] as const,
    },
    // A page rule test that produced a result. Mirrors Ember's in_app
    // page_rule_test_used; `result` is 'match' or 'no_match'.
    pageRuleTestUsed: {
      name: 'In-App: Page Rule Test Used',
      properties: ['Message Used In', 'In-App Content ID', 'Status', 'platform', 'result'] as const,
    },
    // The app targeting's older-SDK notice "Learn more" link. Mirrors Ember's
    // in_app sdk_notice_link_clicked; `targeting_app_id` is the targeted app.
    sdkNoticeLinkClicked: {
      name: 'In-App: SDK Notice Link Clicked',
      properties: ['Message Used In', 'In-App Content ID', 'Status', 'targeting_app_id'] as const,
    },
    // Sent from the sign-up forms table's row menu, with the same names as the Ember anonymous messages list.
    contentDuplicateStarted: {
      name: 'In-App Content: Duplicate Started',
      properties: ['In-App Content ID', 'Status'] as const,
    },
    contentDuplicateCompleted: {
      name: 'In-App Content: Duplicate Completed',
      properties: ['In-App Content ID', 'Status'] as const,
    },
    contentDeleteStarted: {
      name: 'In-App Content: Delete Started',
      properties: ['In-App Content ID', 'Status'] as const,
    },
  },
  // Notification inbox settings.
  inbox: {
    inboxNamed: {
      name: 'Inbox: Named',
      properties: [] as const,
    },
    inboxPublished: {
      name: 'Inbox: Published',
      properties: [] as const,
    },
  },
  // Push channel settings and the workspace's mobile apps. Property names are
  // the ones the dashboards already group on, which is why the casing is
  // inconsistent between events.
  push: {
    // Fired per resolved load of the push settings page, carrying whether each
    // platform is currently able to send.
    messageSettingsViewed: {
      name: 'Push: Message Settings Viewed',
      properties: ['Android Push Status', 'iOS Push Status'] as const,
    },
    // One platform's configuration screen. `Source` names the screen it was
    // opened from, and is absent when it was opened from the settings page.
    configurationViewed: {
      name: 'Push: Configuration Viewed',
      properties: ['platform'] as const,
      optionalProperties: ['Source'] as const,
    },
    setupStarted: {
      name: 'Push: Setup Started',
      properties: ['Platform'] as const,
      optionalProperties: ['Source'] as const,
    },
    // `provider` is the upper-cased key ('APNS' / 'FCM').
    authorizationAdded: {
      name: 'Push: Authorization Added',
      properties: ['platform', 'provider'] as const,
    },
    authorizationUpdated: {
      name: 'Push: Authorization Updated',
      properties: ['platform', 'provider'] as const,
    },
    pushDisabled: {
      name: 'Push: Push Disabled',
      properties: ['platform', 'provider'] as const,
    },
    // The notice shown instead of the delete confirmation when an app still has
    // messages sending through it.
    appDeletionWarningViewed: {
      name: 'App Deletion Warning Viewed',
      properties: ['app_id', 'affected_message_count'] as const,
    },
  },
  // API-credentials page analytics mirror the Ember events (for dashboard
  // continuity): the same events live under `integration` (account level) and
  // `workspace` (workspace level), differing only in the wire-name prefix.
  integration: {
    // Directory card click. `Integration Sub-Category` (subtitle) and
    // `Integration Status` (enabled/disabled) are only sent when applicable.
    clicked: {
      name: 'Integration: Clicked',
      properties: ['Source', 'Integration Name', 'Integration Category', 'Integration Type'] as const,
      optionalProperties: ['Integration Sub-Category', 'Integration Status'] as const,
    },
    // Directory search + category filtering (the type filter fires nothing).
    listSearchedOrFiltered: {
      name: 'Integration: List Searched or Filtered',
      properties: ['Source', 'List Searched', 'List Filtered by Category', 'List Filtered by Type'] as const,
      optionalProperties: ['Integration Category', 'Integration Type', 'Integration Search Value'] as const,
    },
    // `Source` names the page the copy happened on. The settings panels don't
    // send it (they are the only page for their credentials); the connector
    // pages do, because several of them show the same tracking credentials.
    apiCredentialsCopied: {
      name: 'Integration: API Credentials Copied',
      properties: ['API Credential Type', 'API Credential Copied'] as const,
      optionalProperties: ['Source'] as const,
    },
    apiCredentialsCreationStarted: {
      name: 'Integration: API Credentials Creation Started',
      properties: ['API Credential Type'] as const,
    },
    apiCredentialsDeletionStarted: {
      name: 'Integration: API Credentials Deletion Started',
      properties: ['API Credential Type'] as const,
    },
    trackingApiKeysViewed: {
      name: 'Integration: Tracking API Keys Viewed',
      properties: [] as const,
    },
    appApiKeysViewed: {
      name: 'Integration: App API Keys Viewed',
      properties: [] as const,
    },
    // Reporting-webhook events. `Integration Sub-Category` is part of the wire
    // contract for the enable/disable pair but the reporting-webhook call sites
    // never send it, so it stays optional here.
    //
    // `Integration Disabled or Deleted` is deliberately optional and only sent
    // when the action empties the workspace: disabling the last *enabled*
    // webhook, or deleting the only webhook.
    //
    // Enable Started has two shapes. The list row and the form's state toggle
    // name the surface they fired from. Opening the create page also fires it,
    // for a webhook that does not exist yet — so that one carries no source, and
    // names the transition only when the workspace is getting its very first
    // webhook. Everything beyond the integration name and category is therefore
    // optional.
    //
    // Generic across connectors — reporting webhooks and mParticle both fire
    // these. Reporting webhooks pass no `Webhook Endpoint ID` on enable: Ember's
    // `enableStarted` transform drops the id it is handed, so that event has
    // never identified an endpoint. `Integration: Disable Started` does carry one.
    integrationEnableStarted: {
      name: 'Integration: Enable Started',
      properties: ['Integration Name', 'Integration Category'] as const,
      optionalProperties: ['Integration Sub-Category', 'Integration Enabled or Re-Enabled', 'Source'] as const,
    },
    // The webhook-specific fields are optional because the event is shared: a
    // reporting webhook identifies the endpoint it disabled, and mParticle has no
    // endpoint to name. Reporting webhooks still send all of them, and
    // `reporting-webhooks/analytics.test.ts` asserts the exact payloads, so they
    // cannot quietly stop.
    integrationDisableStarted: {
      name: 'Integration: Disable Started',
      properties: ['Integration Name', 'Integration Category'] as const,
      optionalProperties: [
        'Integration Sub-Category',
        'Integration Disabled or Deleted',
        'Webhook Endpoint ID',
        'Webhook Endpoint Disabled or Deleted',
        'Source',
      ] as const,
    },
    // The completion halves of the enable/disable pair, and the verify pair the
    // Segment destination connector adds. Ember fired all four from
    // `pods/integrations/segment_in/controller.ts`; only the two "Started" events
    // above had been ported, because no migrated connector had a verify step or
    // reported its own completion until now.
    integrationEnabled: {
      name: 'Integration: Enabled',
      properties: ['Integration Name', 'Integration Category'] as const,
      optionalProperties: ['Integration Sub-Category', 'Integration Enabled or Re-Enabled'] as const,
    },
    integrationDisabled: {
      name: 'Integration: Disabled',
      properties: ['Integration Name', 'Integration Category'] as const,
      optionalProperties: ['Integration Sub-Category', 'Integration Disabled or Deleted'] as const,
    },
    // Fired when a confirmation opens, not when it completes — the pair to
    // `Integration: Disable Started` for the clear action, which has no completion
    // event because it never touches Segment.
    integrationClearStarted: {
      name: 'Integration: Clear Started',
      properties: ['Integration Name', 'Integration Category'] as const,
      optionalProperties: ['Integration Sub-Category'] as const,
    },
    integrationVerifyStarted: {
      name: 'Integration: Verify Started',
      properties: ['Integration Name', 'Integration Category'] as const,
      optionalProperties: ['Integration Sub-Category'] as const,
    },
    // `Integration Verify Status` is the outcome the reader was shown. It is
    // optional because a verify that resolves with no message to report sends none
    // rather than an empty string.
    integrationVerifyCompleted: {
      name: 'Integration: Verify Completed',
      properties: ['Integration Name', 'Integration Category'] as const,
      optionalProperties: ['Integration Sub-Category', 'Integration Verify Status'] as const,
    },
    // The catalog detail page's two click events. Ember's transforms read `Source`
    // from a `from` field that the detail page never sets on the button event, so
    // it stays optional there; the use-case link event always sends 'In-App'.
    integrationLandingPageButtonClicked: {
      name: 'Integration: Landing Page Button Clicked',
      properties: ['Integration Name', 'Integration Category', 'Integration Type', 'Integration Clicked On'] as const,
      optionalProperties: ['Source'] as const,
    },
    integrationUseCaseLinkClicked: {
      name: 'Integration: Use Case Link Clicked',
      properties: [
        'Source',
        'Integration Name',
        'Integration Category',
        'Integration Type',
        'Integration Link URL',
      ] as const,
    },
    reportingWebhookEditStarted: {
      name: 'Integration: Edit Started',
      properties: ['Integration Name', 'Integration Category', 'Webhook Endpoint ID', 'Source'] as const,
    },
    reportingWebhookKeyCopied: {
      name: 'Integration: Reporting Webhook Key Copied',
      properties: ['Webhook Endpoint ID', 'Source'] as const,
    },
    // Fires on both outcomes of a "Send test". `Test Response` carries the server's error detail
    // and is only present on a failure, so it is optional rather than sent empty on success.
    reportingWebhookTestSent: {
      name: 'Integration: Reporting Webhook Test Sent',
      properties: ['Test Passed', 'Webhook Endpoint ID'] as const,
      optionalProperties: ['Test Response'] as const,
    },
    // A setup flow stopped short of connecting something. `Integration Step`
    // names the step it stopped on and `Error` the message the person saw,
    // without any link it carried.
    errorEncountered: {
      name: 'Integration: Error Encountered',
      properties: ['Integration Name', 'Integration Step', 'Error', 'Source'] as const,
    },
  },
  // Workspace-level variant of the api-credentials events. A dedicated category
  // (rather than the shared `workspace` one) keeps these required-prop events out
  // of the dynamic `track('workspace', …)` call sites elsewhere.
  workspaceApiCredentials: {
    apiCredentialsCopied: {
      name: 'Workspace: API Credentials Copied',
      properties: ['API Credential Type', 'API Credential Copied'] as const,
    },
    apiCredentialsCreationStarted: {
      name: 'Workspace: API Credentials Creation Started',
      properties: ['API Credential Type'] as const,
    },
    apiCredentialsDeletionStarted: {
      name: 'Workspace: API Credentials Deletion Started',
      properties: ['API Credential Type'] as const,
    },
    trackingApiKeysViewed: {
      name: 'Workspace: Tracking API Keys Viewed',
      properties: [] as const,
    },
    appApiKeysViewed: {
      name: 'Workspace: App API Keys Viewed',
      properties: [] as const,
    },
  },
  multiAccount: {
    viewedAccounts: {
      name: 'Multi-Account: User Viewed Accounts',
      properties: ['Session Count'] as const,
    },
    switchedAccounts: {
      name: 'Multi-Account: User Switched Accounts',
      properties: ['Session Count', 'Current Account ID', 'New Account Name'] as const,
    },
    clickedAddAccount: {
      name: 'Multi-Account: User Clicked Add Account',
      properties: ['Session Count'] as const,
    },
    loggedOutAccount: {
      name: 'Multi-Account: User Logged Out Of Account',
      properties: ['Session Count', 'Logged-Out Account Name', 'Was Current'] as const,
    },
  },
  partner: {
    // Top-of-funnel intention (opening the create form). The completed create is
    // tracked server-side as "Partner: Account Created" (services), so the gap
    // between the two is form abandonment + backend failures.
    createAccountClicked: {
      name: 'Partner: Create Account Clicked',
      properties: [] as const,
    },
    accountOpened: {
      name: 'Partner: Client Account Opened',
      properties: ['Account ID'] as const,
    },
  },
  googleLogins: {
    reviewViewed: {
      name: 'Google Logins: Duplicate Review Viewed',
      properties: ['Duplicate Count'] as const,
    },
  },
  import: {
    uploadStarted: {
      name: 'Import: Upload Started',
      properties: ['source'] as const,
      optionalProperties: ['dragAndDrop'] as const,
    },
    listViewed: {
      name: 'Import: List Viewed',
      properties: [] as const,
    },
    canceled: {
      name: 'Import: Canceled',
      properties: [] as const,
      // All three can be absent. `Source` is only reported for the steps the embedded app has a
      // label for; the id and type are absent on the upload screen, where the import has not
      // been created and may not yet have a type.
      optionalProperties: ['Source', 'Import ID', 'Import Type'] as const,
    },
    exampleFileExperimentViewed: {
      name: 'Import: Example File Experiment Viewed',
      properties: ['Is Trial'] as const,
    },
    exampleFileDownloaded: {
      name: 'Import: Example File Downloaded',
      properties: ['Example File Name', 'Source'] as const,
    },
    videoStarted: {
      name: 'Import: Video Started',
      properties: [] as const,
    },
    uploadViewed: {
      name: 'Import: Upload CSV Tab Viewed',
      properties: [] as const,
      // Absent on the new-import screen, where there is no import yet and no type chosen.
      optionalProperties: ['Import ID', 'Import Type'] as const,
    },
    exampleLinkClicked: {
      name: 'Import: Example File Clicked',
      properties: [] as const,
      optionalProperties: ['Import ID', 'Import Type'] as const,
    },
    uploadDeleted: {
      name: 'Import: Upload Deleted',
      properties: ['Upload Type'] as const,
      optionalProperties: ['Import ID', 'Import Type'] as const,
    },
    replaceClicked: {
      name: 'Import: Replace Clicked',
      properties: ['Upload Type'] as const,
      optionalProperties: ['Import ID', 'Import Type'] as const,
    },
    eventColumnWarning: {
      name: 'Import: Event Column Warning',
      properties: ['Import ID', 'Import Type', 'Column'] as const,
    },
    eventPreviewViewed: {
      name: 'Import: Event Preview Tab Viewed',
      properties: ['Import ID', 'Import Type'] as const,
    },
    mapFieldsViewed: {
      name: 'Import: Map Fields Tab Viewed',
      properties: ['Import ID', 'Import Type'] as const,
    },
    // One event for both directions: the embedded app registers `attribute_mapped` and
    // `attribute_skipped` under the same name and tells them apart by `Mapping Change Type`.
    attributeMappingChanged: {
      name: 'Import: Attribute Mapped Column Changed',
      properties: ['Import ID', 'Import Type', 'Attribute', 'Mapping Change Type'] as const,
    },
    fieldWarningDisplayed: {
      name: 'Import: Field Warning Displayed',
      properties: ['Import ID', 'Import Type', 'Upload Type', 'Warning', 'Fields Available'] as const,
    },
    originalFileExported: {
      name: 'Import: Original File Exported',
      properties: ['Import ID'] as const,
    },
    warningsFileExported: {
      name: 'Import: Warnings File Exported',
      properties: ['Import ID'] as const,
    },
    errorsFileExported: {
      name: 'Import: Errors File Exported',
      properties: ['Import ID'] as const,
    },
    reviewViewed: {
      name: 'Import: Review Tab Viewed',
      properties: ['Import ID', 'Import Type'] as const,
      // Every count comes off the validation summary, which an import that has not validated has
      // none of — the embedded app reports the id and the type alone in that case.
      optionalProperties: [
        'Imported New People',
        'Updated Existing People',
        'Email Changes',
        'Warnings',
        'Errors',
        'Error Content',
        'Event Cardinality',
        'Total Events',
      ] as const,
    },
    previewFileExported: {
      name: 'Import: Final Import Previewed or Downloaded',
      properties: ['Import ID', 'Import Type'] as const,
      // Every count comes off the validation summary, which an import that has not validated has
      // none of — the embedded app reports the two ids alone in that case.
      optionalProperties: [
        'Imported New People',
        'Updated Existing People',
        'Email Changes',
        'Warnings',
        'Errors',
        'Event Cardinality',
        'Total Events',
      ] as const,
    },
  },
  collections: {
    creationStarted: {
      name: 'Collections: Creation Started',
      properties: [] as const,
    },
    listViewed: {
      name: 'Collections: List Viewed',
      properties: [] as const,
    },
    deleteStarted: {
      name: 'Collections: Delete Started',
      properties: ['Collection ID', 'Collection Size'] as const,
    },
    updateStarted: {
      name: 'Collections: Update Started',
      properties: ['Collection ID', 'Collection Size'] as const,
    },
  },
  account: {
    verificationReviewInboxLinkClicked: {
      name: 'Account: Verification Review Inbox Link Clicked',
      properties: ['MX Provider', 'Email Domain'] as const,
    },
    teamMemberInvitationResent: {
      name: 'Account: Team Member Invitation Resent',
      properties: ['teamMemberEmail'] as const,
    },
    guestInvitationResent: {
      name: 'Account: Guest Invitation Resent',
      properties: ['guestEmail'] as const,
    },
    teamMemberChangedToGuest: {
      name: 'Account: Team Member Changed To Guest',
      properties: ['teamMemberEmail', 'guestEmail'] as const,
    },
    permissionsMatched: {
      name: 'Account: Permissions Matched',
      properties: ['is_guest', 'matched_role'] as const,
    },
    guestInvited: {
      name: 'Account: Guest Invited',
      properties: [] as const,
    },
    cancellation_started: {
      name: 'Account: Cancellation Started',
      properties: [] as const,
    },
    offer_help_continue_to_cancel: {
      name: 'Account: Offer Help Continue To Cancel',
      properties: [] as const,
    },
    offer_help_get_help: {
      name: 'Account: Offer Help Get Help',
      properties: [] as const,
    },
    billing_receipt_download: {
      name: 'Account: Billing History Downloaded',
      properties: [] as const,
    },
    billing_invoice_download: {
      name: 'Account: Billing Invoice Downloaded',
      properties: [] as const,
    },
    retention_discount_offered: {
      name: 'Account: Retention Discount Offered',
      properties: [] as const,
    },
    retention_discount_claimed: {
      name: 'Account: Retention Discount Claimed',
      properties: [] as const,
    },
    retention_discount_declined: {
      name: 'Account: Retention Discount Declined',
      properties: [] as const,
    },
    builder_downgrade_offered: {
      name: 'Account: Builder Downgrade Offered',
      properties: [] as const,
    },
    builder_downgrade_declined: {
      name: 'Account: Builder Downgrade Declined',
      properties: [] as const,
    },
    builder_downgrade_confirm_viewed: {
      name: 'Account: Builder Downgrade Confirm Viewed',
      properties: [] as const,
    },
    builder_downgrade_accepted: {
      name: 'Account: Builder Downgrade Accepted',
      properties: [] as const,
    },
    // Keep this property name. The reactivation funnel reports on it, and a new name loses that history.
    canceled_state_viewed: {
      name: 'Account: Canceled State Viewed',
      properties: ['Reactivate Option Shown'] as const,
    },
    reactivation_started: {
      name: 'Account: Reactivation Started',
      properties: [] as const,
    },
    reactivation_completed: {
      name: 'Account: Reactivation Completed',
      properties: [] as const,
    },
    requireTwoFactorEnableStarted: {
      name: 'Account: Enable Require 2FA Started',
      properties: [] as const,
    },
    requireTwoFactorDisableStarted: {
      name: 'Account: Disable Require 2FA Started',
      properties: [] as const,
    },
    securitySettingsViewed: {
      name: 'Account: Security Settings Viewed',
      properties: [] as const,
    },
    settings_viewed: {
      name: 'Account: Settings Viewed',
      properties: [] as const,
    },
    your_plan_viewed: {
      name: 'Account: Your Plan Viewed',
      properties: [] as const,
    },
    billing_history_viewed: {
      name: 'Account: Billing History Viewed',
      properties: [] as const,
    },
    available_plans_viewed: {
      name: 'Account: Available Plans Viewed',
      properties: [] as const,
    },
    payment_methods_viewed: {
      name: 'Account: Payment Methods Viewed',
      properties: [] as const,
    },
    configureSsoViewed: {
      name: 'Account: Configure SSO Viewed',
      properties: [] as const,
    },
    authenticateSsoStarted: {
      name: 'Account: Authenticate SSO Started',
      properties: ['Identity Provider'] as const,
    },
    authenticateSsoSubmitted: {
      name: 'Account: Authenticate SSO Submitted',
      // 'Metadata URL' only carries a value for SAML; Ember's shared
      // `ssoIdentityProvider` transform emitted the key on every provider.
      properties: ['Identity Provider', 'Metadata URL'] as const,
    },
    authenticateSsoFailed: {
      name: 'Account: Authenticate SSO Failed',
      properties: ['Identity Provider'] as const,
    },
    disableSsoStarted: {
      name: 'Account: Disable SSO Started',
      properties: ['Identity Provider'] as const,
    },
    samlSpCertDownloadSuccess: {
      name: 'Account: SAML SSO SP Certificate Downloaded',
      properties: [] as const,
    },
    samlSpCertDownloadFailed: {
      name: 'Account: SAML SSO SP Certificate Download Failed',
      properties: [] as const,
    },
  },
  salesforce: {
    syncConfigTabChanged: {
      name: 'Salesforce: Sync Config Tab Changed',
      properties: ['source_id', 'record_type', 'workspace_id', 'from_tab', 'to_tab'] as const,
    },
    queryValidationAttempted: {
      name: 'Salesforce: Query Validation Attempted',
      properties: ['source_id', 'sobject_name', 'workspace_id', 'where_clause_length'] as const,
    },
    queryValidationFailed: {
      name: 'Salesforce: Query Validation Failed',
      properties: [
        'source_id',
        'sobject_name',
        'workspace_id',
        'where_clause_length',
        'error_type',
        'error_message',
      ] as const,
    },
    queryValidationSucceeded: {
      name: 'Salesforce: Query Validation Succeeded',
      properties: ['source_id', 'sobject_name', 'workspace_id', 'where_clause_length', 'record_count'] as const,
    },
    filterButtonClicked: {
      name: 'Salesforce: Filter Button Clicked',
      properties: ['source_id', 'record_type', 'workspace_id', 'button_location'] as const,
    },
  },
  sources: {
    syncSetupCompleted: {
      name: 'Sources: Sync Setup Completed',
      properties: [
        'workspace_id',
        'integration_name',
        'source_id',
        'record_type',
        'transformation_type',
        'setup_flow',
        'template',
        'completion_type',
        'destinations_count',
      ] as const,
    },
    syncTemplateOpened: {
      name: 'Sources: Sync Template Opened',
      properties: ['workspace_id', 'integration_name', 'source_id', 'template'] as const,
    },
    syncTemplateAbandoned: {
      name: 'Sources: Sync Template Abandoned',
      properties: ['workspace_id', 'integration_name', 'source_id', 'record_type', 'template'] as const,
    },
    // Source-adoption funnel events (CDP-6384), Databricks-first but generic
    // across source types. The database connection test runs server-side during
    // config create/update, so its pass/fail events fire on the mutation
    // outcome. Pass and fail are separate named events (not one event with a
    // boolean) so they are individually discoverable in the Mixpanel picker.
    sourceTypeSelected: {
      name: 'Sources: Source Type Selected',
      properties: ['workspace_id', 'integration_name', 'category', 'entry'] as const,
    },
    databaseConnectionTestInitiated: {
      name: 'Sources: Database Connection Test Initiated',
      properties: ['workspace_id', 'integration_name', 'flow'] as const,
    },
    databaseConnectionTestPassed: {
      name: 'Sources: Database Connection Test Passed',
      properties: ['workspace_id', 'integration_name', 'flow'] as const,
    },
    databaseConnectionTestFailed: {
      name: 'Sources: Database Connection Test Failed',
      properties: ['workspace_id', 'integration_name', 'flow', 'error_category'] as const,
    },
    databaseConfigCreated: {
      name: 'Sources: Database Config Created',
      properties: ['workspace_id', 'integration_name'] as const,
    },
    // AI-assisted and manual source setup comparison. These events carry only source
    // identity and UI context. Prompt text, write keys, repository details,
    // OAuth tokens, connection strings, and database credentials never belong
    // in this contract.
    aiSetupPromptCopied: {
      name: 'Sources: AI Setup Prompt Copied',
      properties: [
        'workspace_id',
        'source_id',
        'source_option_id',
        'integration_name',
        'source_type',
        'entry_point',
        'setup_session_id',
        'copy_method',
        'coding_agent',
      ] as const,
    },
    manualSetupSnippetCopyClicked: {
      name: 'Sources: Manual Setup Snippet Copy Clicked',
      properties: [
        'workspace_id',
        'source_id',
        'source_option_id',
        'integration_name',
        'source_type',
        'entry_point',
        'setup_session_id',
      ] as const,
    },
    aiSetupCodingAgentSelected: {
      name: 'Sources: AI Setup Coding Agent Selected',
      properties: [
        'workspace_id',
        'source_id',
        'source_option_id',
        'integration_name',
        'source_type',
        'entry_point',
        'setup_session_id',
        'coding_agent',
        'copy_succeeded',
      ] as const,
    },
    aiSetupSourceLinked: {
      name: 'Sources: AI Setup Source Linked',
      properties: [
        'workspace_id',
        'source_id',
        'source_option_id',
        'integration_name',
        'source_type',
        'entry_point',
        'setup_session_id',
      ] as const,
    },
  },
  aiQueryGeneration: {
    feedbackPositive: {
      name: 'AI Query Generation: Feedback Positive',
      properties: ['source', 'query', 'request', 'sobject_name'] as const,
    },
    feedbackNegative: {
      name: 'AI Query Generation: Feedback Negative',
      properties: ['source', 'query', 'request', 'sobject_name'] as const,
    },
  },
  snippets: {
    // Property keys mirror the wire payload Ember's `snippets`
    // transforms produce — see `ui/app/utils/analytics/transforms/snippets.ts`.
    listViewed: {
      // Lives under `workspace.snippets_viewed` in the Ember `events` map; kept
      // here so the snippet-page tracking is colocated. Wire name is unchanged.
      name: 'Workspace: Snippets Viewed',
      properties: [] as const,
    },
    creationStarted: {
      name: 'Snippet: Creation Started',
      properties: [] as const,
    },
    listSorted: {
      name: 'Snippet: List Sorted',
      properties: ['Search Term', 'Search Results', 'Sort By', 'Sort Order'] as const,
    },
    listFiltered: {
      name: 'Snippet: List Filtered',
      properties: ['Filtered by Object Types'] as const,
    },
    detailPageViewed: {
      name: 'Snippet: Detail Page Viewed',
      properties: ['Snippet ID', 'Snippet Name', 'Snippet Usage Count Total'] as const,
    },
  },
  assetLibrary: {
    folderViewed: {
      name: 'Asset Library: Folder Viewed',
      properties: ['folder_name', 'folder_id', 'folder_count', 'asset_count'] as const,
    },
    searchStarted: {
      name: 'Asset Library: Search Started',
      properties: ['search_query'] as const,
    },
    pagination: {
      name: 'Asset Library: Pagination',
      properties: ['page', 'total_pages'] as const,
    },
    folderNavigation: {
      name: 'Asset Library: Folder Navigation',
      properties: ['folder_name', 'folder_id'] as const,
    },
    fileSelected: {
      name: 'Asset Library: File Selected',
      properties: ['asset_id', 'file_type', 'folder_name', 'folder_id'] as const,
    },
    folderCreated: {
      name: 'Asset Library: Folder Created',
      properties: ['folder_name', 'parent_folder_id'] as const,
    },
    uploadStarted: {
      name: 'Asset Library: Upload Started',
      properties: ['parent_folder_id', 'file_count'] as const,
    },
    uploadCompleted: {
      name: 'Asset Library: Upload Completed',
      properties: ['asset_id', 'file_type', 'parent_folder_id'] as const,
    },
    uploadReused: {
      name: 'Asset Library: Upload Reused',
      properties: ['asset_id', 'parent_folder_id'] as const,
    },
  },
  assetOptimizer: {
    optimizeImageFile: {
      name: 'Asset Optimizer: Optimize Image File',
      properties: ['Builder Type'] as const,
    },
    optimizeImage: {
      name: 'Asset Optimizer: Optimize Image',
      properties: ['Builder Type', 'original_size'] as const,
    },
    saveOptimizedImage: {
      name: 'Asset Optimizer: Save Optimized Image',
      properties: [
        'view_type',
        'compression_type',
        'retina_mode',
        'Builder Type',
        'original_size',
        'optimized_size',
        'savings_percentage',
      ] as const,
    },
    tryOptimizing: {
      name: 'Asset Optimizer: Try Optimizing',
      properties: ['Builder Type'] as const,
    },
  },
  asset: {
    uploaded: {
      name: 'Asset: Uploaded',
      properties: ['id', 'path'] as const,
    },
  },
  campaign: {
    // The three properties below are what every catalogued campaign event
    // carries. 'Campaign Type' is the label for the trigger type, not the wire
    // value, and an object or relationship campaign reports its trigger's
    // subtype (e.g. 'Object Created') rather than the bare type.
    saved: {
      name: 'Campaign: Saved',
      properties: ['Campaign ID', 'Campaign Type', 'Campaign State'] as const,
    },
    // A note dragged out of the workflow toolbar. Nothing is reported when it lands.
    addNoteStarted: {
      name: 'Campaign: Add Note Started',
      properties: ['Campaign ID', 'Campaign Type', 'Campaign State'] as const,
    },
    // The delete button on a note, and its confirmation. The Delete key deletes without either.
    deleteNoteStarted: {
      name: 'Campaign: Delete Note Started',
      properties: ['Campaign ID', 'Campaign Type', 'Campaign State', 'Campaign Note ID'] as const,
    },
    deleteNoteConfirmed: {
      name: 'Campaign: Delete Note Confirmed',
      properties: ['Campaign ID', 'Campaign Type', 'Campaign State', 'Campaign Note ID'] as const,
    },
    moveNoteStarted: {
      name: 'Campaign: Move Note Started',
      properties: ['Campaign ID', 'Campaign Type', 'Campaign State', 'Campaign Note ID'] as const,
    },
    moveNoteCompleted: {
      name: 'Campaign: Move Note Completed',
      properties: ['Campaign ID', 'Campaign Type', 'Campaign State', 'Campaign Note ID'] as const,
    },
    // A note's text edited: the edit starting, and its changed text saved.
    editNoteStarted: {
      name: 'Campaign: Edit Note Started',
      properties: ['Campaign ID', 'Campaign Type', 'Campaign State', 'Campaign Note ID'] as const,
    },
    editNoteCompleted: {
      name: 'Campaign: Edit Note Completed',
      properties: ['Campaign ID', 'Campaign Type', 'Campaign State', 'Campaign Note ID'] as const,
    },
    // Bold, italic or the list pressed in a note's toolbar.
    noteFormattingUsed: {
      name: 'Campaign: Note Formatting Used',
      properties: ['Campaign ID', 'Campaign Type', 'Campaign State', 'Campaign Note ID'] as const,
    },
    // A link added to a note's text, removed from it, or opened from the note.
    noteLinkAdded: {
      name: 'Campaign: Note Link Added',
      properties: ['Campaign ID', 'Campaign Type', 'Campaign State', 'Campaign Note ID'] as const,
    },
    noteLinkRemoved: {
      name: 'Campaign: Note Link Removed',
      properties: ['Campaign ID', 'Campaign Type', 'Campaign State', 'Campaign Note ID'] as const,
    },
    noteLinkOpened: {
      name: 'Campaign: Note Link Opened',
      properties: ['Campaign ID', 'Campaign Type', 'Campaign State', 'Campaign Note ID'] as const,
    },
    // A note's colour picker opened, and a new colour picked from it. Today's
    // colours are named as 'v2-yellow' and so on, earlier palettes' as
    // 'yellow-300' and so on, and any other colour is given as stored.
    noteColorChangeStarted: {
      name: 'Campaign: Note Color Change Started',
      properties: ['Campaign ID', 'Campaign Type', 'Campaign State', 'Campaign Note ID', 'Original Color'] as const,
    },
    noteColorChangeCompleted: {
      name: 'Campaign: Note Color Change Completed',
      properties: [
        'Campaign ID',
        'Campaign Type',
        'Campaign State',
        'Campaign Note ID',
        'Original Color',
        'New Color',
      ] as const,
    },
    // A note's bottom-right corner picked up, and let go at a new size.
    resizeNoteStarted: {
      name: 'Campaign: Resize Note Started',
      properties: ['Campaign ID', 'Campaign Type', 'Campaign State', 'Campaign Note ID'] as const,
    },
    resizeNoteCompleted: {
      name: 'Campaign: Resize Note Completed',
      properties: ['Campaign ID', 'Campaign Type', 'Campaign State', 'Campaign Note ID'] as const,
    },
    editDiscarded: {
      name: 'Campaign: Edit(s) Discarded',
      properties: ['Campaign ID', 'Campaign Type', 'Campaign State', 'Edit(s) Discarded On'] as const,
    },
    // Reported when a trigger type is picked in the trigger editor. 'Selected
    // Trigger' is the card's own name where a type spans several cards, and the
    // type's name otherwise, so the funnel separates the cards a user chose
    // between.
    triggerTabViewed: {
      name: 'Campaign: Trigger Tab Viewed',
      properties: ['Campaign ID', 'Campaign Type', 'Campaign State', 'Selected Trigger', 'Trigger Tab Type'] as const,
    },
    // The webhook trigger's four events. 'Source' names the surface the copy or
    // the received data happened on, so the funnel separates the trigger
    // fly-out from the other places a webhook URL is shown.
    webhookUrlCopied: {
      name: 'Campaign: Webhook URL Copied',
      properties: ['Campaign ID', 'Campaign Type', 'Campaign State', 'Source'] as const,
    },
    // The user agent and address of whatever sent the webhook. Both come off
    // the log's first source, and a log can arrive carrying neither, so they
    // are optional rather than sent empty.
    webhookTestDataReceived: {
      name: 'Campaign: Webhook Test Data Received',
      properties: ['Campaign ID', 'Campaign Type', 'Campaign State', 'Source'] as const,
      optionalProperties: ['Webhook User Agent', 'Webhook IP Address'] as const,
    },
    dataWorkflowWebhooksModalViewed: {
      name: 'Campaign: Data Workflow Webhooks Modal Viewed',
      properties: [] as const,
    },
    // Two entries under one wire name, for the reason the reporting-webhook
    // pair above gives: a click on an example link reports where it went and a
    // click on the video reports that it was the video, and the registry
    // requires every listed property from every caller, so one entry could not
    // hold both shapes.
    dataWorkflowWebhooksModalLinkClicked: {
      name: 'Campaign: Data Workflow Webhooks Modal Clicked',
      properties: ['Destination URL'] as const,
    },
    dataWorkflowWebhooksModalVideoClicked: {
      name: 'Campaign: Data Workflow Webhooks Modal Clicked',
      properties: ['Video Clicked'] as const,
    },
    // The campaign Message settings panel's rate limit section, reported once
    // as it opens. 'Currently Enabled' is whether the saved rate limit is on.
    messageRateLimitViewed: {
      name: 'Campaign: Message Rate Limit Viewed',
      properties: ['Campaign ID', 'Campaign Type', 'Campaign State', 'Currently Enabled'] as const,
    },
    // A random cohort branch's "Cohort by" select, on each pick. 'Type' is
    // 'Object' for an object type and 'Profile' for the profile itself.
    randomCohortBranchObjectTypeSelect: {
      name: 'Campaign: Random Cohort Branch Object Type Select',
      properties: [
        'Campaign ID',
        'Campaign Type',
        'Campaign State',
        'Object Type ID',
        'Object Type Name',
        'Type',
      ] as const,
    },
    // A click on a step that is already in the workflow: a card or an exit on
    // the canvas, or a variation's Edit button in an A/B test's panel. 'Tray
    // Tab' is 'Build' or 'Metrics'; 'Language Count' is how many other
    // language versions the step has. A click from the canvas carries
    // 'Source' as 'Workflow Item'; one from a panel carries none.
    workflowExistingItemClicked: {
      name: 'Campaign: Workflow Existing Item Clicked',
      properties: [
        'Campaign ID',
        'Campaign Type',
        'Campaign State',
        'Campaign Action ID',
        'Workflow Item Type',
        'Tray Tab',
        'Language Count',
      ] as const,
      optionalProperties: ['Source'] as const,
    },
    // The workflow canvas's Measure panel: opened from the toolbar or its key,
    // then each change to the measures it shows or the window it reads.
    // 'Selected Metrics' is the measures after the change, each capitalized.
    workflowMeasureClicked: {
      name: 'Campaign: Workflow Measure Clicked',
      properties: ['Campaign ID', 'Campaign Type', 'Campaign State'] as const,
    },
    workflowMetricsTabChosenMetricsChanged: {
      name: 'Campaign: Workflow Metrics Tab Chosen Metrics Changed',
      properties: [
        'Campaign ID',
        'Campaign Type',
        'Campaign State',
        'Filtered Timeframe',
        'Filtered Timeframe Length',
        'Selected Metrics',
      ] as const,
    },
    workflowMetricsTabTimePeriodChanged: {
      name: 'Campaign: Workflow Metrics Tab Time Period Changed',
      properties: [
        'Campaign ID',
        'Campaign Type',
        'Campaign State',
        'Filtered Timeframe',
        'Filtered Timeframe Length',
        'Selected Metrics',
      ] as const,
    },
    // The template picker offered under the trigger card of an automation with
    // no trigger yet: opened, then a template picked in it. The funnel's last
    // step, `emptyStateTemplateSelected`, reports the template alone.
    chooseTemplateStarted: {
      name: 'Campaign: Choose Template Started',
      properties: ['Campaign ID', 'Campaign Type', 'Campaign State'] as const,
    },
    chooseTemplateCompleted: {
      name: 'Campaign: Choose Template Completed',
      properties: ['Campaign ID', 'Campaign Type', 'Campaign State', 'Template Name'] as const,
    },
    emptyStateTemplateSelected: {
      name: 'Campaign: Empty State Template Selected',
      properties: ['Template Name'] as const,
    },
    // The header overflow menu's record actions, sent when the item is clicked. A Broadcasts
    // row's Duplicate and Delete report the same two.
    duplicateStarted: {
      name: 'Campaign: Duplicate Started',
      properties: ['Campaign ID', 'Campaign Type', 'Campaign State'] as const,
    },
    deleteStarted: {
      name: 'Campaign: Delete Started',
      properties: ['Campaign ID', 'Campaign Type', 'Campaign State'] as const,
    },
    archiveStarted: {
      name: 'Campaign: Archive Started',
      properties: [
        'Campaign ID',
        'Campaign Type',
        'Campaign State',
        'Has Unsent Drafts',
        'Has Journeys In Progress',
      ] as const,
    },
    unarchiveStarted: {
      name: 'Campaign: Unarchive Started',
      properties: ['Campaign ID', 'Campaign Type', 'Campaign State'] as const,
    },
    // Sent when workflow messages change how they send. 'Workflow Bulk Action
    // Initiation Type' names the surface the change came from: 'review' sends
    // it with only the messages whose save went through, and 'action panel'
    // sends it as soon as a sending behavior is picked, before any save, as
    // Ember does.
    workflowExistingItemSendingBehaviorChanged: {
      name: 'Campaign: Workflow Existing Item Sending Behavior Changed',
      properties: [
        'Campaign ID',
        'Campaign Type',
        'Campaign State',
        'Campaign Action IDs',
        'Workflow Item Types',
        'Selected Workflow Item Count',
        'Tray Tab',
        'Workflow Bulk Action Initiation Type',
        'New Sending State',
      ] as const,
    },
    // Sent when a workflow message is turned into an A/B test, once the test
    // is created. 'Workflow Bulk Action Initiation Type' names the surface,
    // such as 'action panel'.
    workflowExistingItemAbTestCreated: {
      name: 'Campaign: Workflow Existing Item A/B Test Created',
      properties: [
        'Campaign ID',
        'Campaign Type',
        'Campaign State',
        'Campaign Action IDs',
        'Workflow Item Types',
        'Selected Workflow Item Count',
        'Tray Tab',
        'Workflow Bulk Action Initiation Type',
      ] as const,
    },
  },
  broadcast: {
    // Reports on every successful load of either Broadcasts tab, so a search, filter, sort or
    // page change reports again; the welcome state reports nothing. Each "Filtered by" key is
    // sent only while that filter is set and its catalog is loaded, and carries the names, not
    // the ids.
    listViewed: {
      name: 'Broadcast: List Viewed',
      properties: ['Broadcast Type', 'Filtered By Broadcast Search'] as const,
      optionalProperties: ['Filtered By Campaign Tag(s)', 'Filtered by Object Types'] as const,
    },
    // The create form, on opening (with the `source` the URL carries, or "Broadcasts"), and once
    // more after a one-time send credits the setup guide's first message, as Ember does. An empty
    // source reports no properties.
    createStarted: {
      name: 'Broadcast: Create Started',
      properties: [] as const,
      optionalProperties: ['Source'] as const,
    },
    // The one-time sends tab's sort picker; `sorting` is the option's label.
    listSorted: {
      name: 'Broadcast: List Sorted',
      properties: ['sorting'] as const,
    },
    sentTabViewed: {
      name: 'Newsletter: Sent Tab Viewed',
      properties: ['Newsletter ID', 'Newsletter Channel'] as const,
    },
    recipientsTabViewed: {
      name: 'Broadcast: Recipients Tab Viewed',
      properties: ['Newsletter ID', 'Newsletter Channel', 'Broadcast Type', 'Broadcast Sent Or Not'] as const,
    },
    // An API-triggered broadcast's recipient conditions discarded in the
    // trigger fly-out. Reported under the newsletter name, which the existing
    // dashboards count, with the campaign's id as 'Newsletter ID'.
    editDiscarded: {
      name: 'Newsletter: Edit(s) Discarded',
      properties: ['Newsletter ID', 'Edit(s) Discarded On'] as const,
    },
    // A one-time send row's Duplicate and Delete. Delete reports on the menu click, before its
    // confirmation.
    copyStarted: {
      name: 'Newsletter: Copy Started',
      properties: ['Newsletter ID', 'Newsletter Channel', 'Languages Count'] as const,
    },
    // A one-time send's Pause sending and Cancel sending, on the menu click.
    pauseSendingStarted: {
      name: 'Newsletter: Pause Sending Started',
      properties: ['Newsletter ID', 'Newsletter Channel'] as const,
    },
    sendingCancelStarted: {
      name: 'Newsletter: Sending Cancel Started',
      properties: ['Newsletter ID', 'Newsletter Channel'] as const,
    },
    resumeSendingStarted: {
      name: 'Newsletter: Resume Sending Started',
      properties: ['Newsletter ID', 'Newsletter Channel'] as const,
    },
    // The Copy to workspace item, on opening its modal.
    copyToWorkspaceStarted: {
      name: 'Newsletter: Copy To Workspace Started',
      properties: ['Newsletter ID', 'Newsletter Channel', 'Languages Count'] as const,
    },
    deleteStarted: {
      name: 'Newsletter: Delete Started',
      properties: ['Newsletter ID', 'Newsletter Channel'] as const,
    },
  },
  workspace: {
    anonymousEventsMergeEnabled: {
      name: 'Workspace: Anonymous Event Merge Enabled',
      properties: [] as const,
    },
    anonymousEventsMergeDisabled: {
      name: 'Workspace: Anonymous Event Merge Disabled',
      properties: [] as const,
    },
    autoMergeEnabled: {
      name: 'Workspace: Multi-identifier Merge Enabled',
      properties: [] as const,
    },
    autoMergeDisabled: {
      name: 'Workspace: Multi-identifier Merge Disabled',
      properties: [] as const,
    },
    autoMergeOnUpdateEnabled: {
      name: 'Workspace: Auto-merge On Identifier Update Enabled',
      properties: [] as const,
    },
    autoMergeOnUpdateDisabled: {
      name: 'Workspace: Auto-merge On Identifier Update Disabled',
      properties: [] as const,
    },
    preserveUnsubscribesOnMergeEnabled: {
      name: 'Workspace: Preserve Unsubscribes On Merge Enabled',
      properties: [] as const,
    },
    preserveUnsubscribesOnMergeDisabled: {
      name: 'Workspace: Preserve Unsubscribes On Merge Disabled',
      properties: [] as const,
    },
    autoIdentifyTrackLinksEnabled: {
      name: 'Workspace: Auto-identify Tracked Links Enabled',
      properties: [] as const,
    },
    autoIdentifyTrackLinksDisabled: {
      name: 'Workspace: Auto-identify Tracked Links Disabled',
      properties: [] as const,
    },
    defaultUnsubscribedOnCreateEnabled: {
      name: 'Workspace: Default Unsubscribed On Create Enabled',
      properties: [] as const,
    },
    defaultUnsubscribedOnCreateDisabled: {
      name: 'Workspace: Default Unsubscribed On Create Disabled',
      properties: [] as const,
    },
    // The geolocation-collection toggle on the time-zone-match settings page.
    // Ember's controller tracked this too, but its catalog never declared the
    // event, so the call silently dropped — registering it here makes the
    // intended event real (FPS-2868).
    geolocationToggled: {
      name: 'Workspace: Geolocation Toggled',
      properties: ['enabled', 'deleted_existing_data'] as const,
    },
    comparePlansClicked: {
      name: 'Workspace: Compare Plans From Workspaces Viewed',
      properties: ['type'] as const,
    },
    deliverySettingsUpdated: {
      name: 'Workspace: Delivery Settings Updated',
      properties: ['Previous Delivery Settings', 'Delivery Settings'] as const,
    },
    deliveryEmailUpdated: {
      name: 'Workspace: Delivery Email Updated',
      properties: [] as const,
    },
    openTrackingEnabled: {
      name: 'Workspace: Open Tracking Enabled',
      properties: [] as const,
    },
    openTrackingDisabled: {
      name: 'Workspace: Open Tracking Disabled',
      properties: [] as const,
    },
    updateIdentifiersByIdChanged: {
      name: 'Workspace: Update Identifiers By ID Changed',
      properties: ['Identify by ID'] as const,
    },
    identifierChangeStarted: {
      name: 'Workspace: Identifier Change Started',
      properties: ['Current Identifier Configuration', 'Migrate to'] as const,
    },
    identifierChangeSubmitted: {
      name: 'Workspace: Identifier Change Submitted',
      properties: ['Current Identifier Configuration', 'Migrate to'] as const,
    },
  },
  plan_billing: {
    switchToPremiumButtonClicked: {
      name: 'Plan and Billing: Switch to Premium Button Clicked',
      properties: ['Account Id', 'Feature Name', 'Requester', 'Requester Email', 'Requester User Id'] as const,
    },
    planActionClicked: {
      name: 'Plan and Billing: Plan Action Clicked',
      properties: ['Account Id', 'Current Plan', 'Action'] as const,
    },
    clickedSubmit: {
      name: 'Plan and Billing: Clicked Submit',
      properties: ['Account Id', 'Current Plan', 'Submission Type'] as const,
    },
    buyOrRequestAccessClicked: {
      name: 'Plan and Billing: Buy or Request Access Clicked',
      properties: ['Account Id', 'Current Plan', 'Requested Plan', 'Text'] as const,
    },
    salesOrContractsLetsTalkClicked: {
      name: 'Plan and Billing: Sales or Contracts Lets Talk Clicked',
      properties: ['Button Name'] as const,
    },
    includedInPlanViewed: {
      name: "Plan and Billing: What's included in my plan viewed",
      properties: ['Account Id', 'Requester', 'Requester Email', 'Requester User Id'] as const,
    },
    limitsBannerLearnMoreClicked: {
      name: 'Plan and Billing: Limits Banner Learn More Clicked',
      properties: ['Account Id', 'Current Plan', 'Banner Id'] as const,
    },
    viewBillingHelp: {
      name: 'Plan and Billing: View Billing Help',
      properties: ['Account Id', 'Current Plan'] as const,
    },
    viewAllFeaturesClicked: {
      name: 'Plan and Billing: View all features clicked',
      properties: [
        'Account Id',
        'Requester',
        'Requester Email',
        'Requester User Id',
        'Current Plan',
        'Viewed Feature List',
      ] as const,
    },
  },
  aiCompliancePrompt: {
    created: {
      name: 'AI Compliance Prompt: Created',
      properties: [] as const,
    },
    removed: {
      name: 'AI Compliance Prompt: Removed',
      properties: [] as const,
    },
    edited: {
      name: 'AI Compliance Prompt: Edited',
      properties: [] as const,
    },
  },
  privacy: {
    pageViewed: {
      name: 'Account: Privacy and Data Viewed',
      properties: [] as const,
    },
    questionsViewed: {
      name: 'Account: Privacy and Data FAQ Viewed',
      properties: [] as const,
    },
    enableProductTrackingStarted: {
      name: 'Account: Enable Product Tracking Started',
      properties: [] as const,
    },
    disableProductTrackingStarted: {
      name: 'Account: Disable Product Tracking Started',
      properties: [] as const,
    },
  },
  languages: {
    settingsTipLinkClicked: {
      name: 'Languages: Language Settings Tips and Resources Link Clicked',
      properties: ['Language Attribute', 'Link'] as const,
    },
    feedbackCanceled: {
      name: 'Languages: Feedback Canceled',
      properties: [] as const,
    },
  },
  user: {
    // Property names are the wire keys the Ember error alert already sends
    // ('Current URL' with the space), so both apps land in one series.
    errorEncountered: {
      name: 'User: Error Encountered',
      properties: ['Message', 'Current URL', 'Source'] as const,
    },
    feedbackRequestStarted: {
      name: 'User: Feedback Request Started',
      properties: ['Source'] as const,
    },
    reAuthenticated: {
      name: 'User: Re Authenticated',
      properties: ['Method'] as const,
    },
    settingsViewed: {
      name: 'User: Personal Settings Viewed',
      properties: [] as const,
    },
    settingsEdited: {
      name: 'User: Settings Edited',
      properties: [] as const,
    },
    // Ember fires this on entering the enrollment flow, via its route-transition
    // analytics map rather than from the route itself.
    twoFaEnablingStarted: {
      name: 'User: 2FA Enabling Started',
      properties: [] as const,
    },
    changeGoogleAccountStarted: {
      name: 'User: Change Google Account Started',
      properties: [] as const,
    },
    unlinkGoogleAccountStarted: {
      name: 'User: Unlink Google Account Started',
      properties: [] as const,
    },
    profileImageAdded: {
      name: 'User: Profile Image Added',
      properties: [] as const,
    },
    profileImageReplaced: {
      name: 'User: Profile Image Replaced',
      properties: [] as const,
    },
    profileImageDeleted: {
      name: 'User: Profile Image Deleted',
      properties: [] as const,
    },
  },
  // The Analysis report. Every wire name and property name here is Ember's,
  // because the funnels built on them are live: see
  // `app/utils/analytics/transforms/analysis.ts` in the Journeys UI.
  analysis: {
    viewed: {
      name: 'Analysis: Viewed',
      properties: [] as const,
    },
    reportStarted: {
      name: 'Analysis: Report Started',
      properties: [
        'Report ID',
        'Data Type',
        'Tag Count',
        'Filtered By Name',
        'Exclude Archived',
        'Filtered Timeframe',
        'Filtered Timeframe Length',
      ] as const,
    },
    reportCompleted: {
      name: 'Analysis: Report Completed',
      properties: [
        'Report ID',
        'Data Type',
        'Tag Count',
        'Filtered By Name',
        'Exclude Archived',
        'Filtered Timeframe',
        'Filtered Timeframe Length',
        'Query Time',
        'Row Count',
      ] as const,
    },
    reportCriteriaChanged: {
      name: 'Analysis: Report Criteria Changed',
      properties: [
        'Report ID',
        'Data Type',
        'Tag Count',
        'Filtered By Name',
        'Exclude Archived',
        'Filtered Timeframe',
        'Filtered Timeframe Length',
        'Edit Type',
      ] as const,
    },
    refineResultsStarted: {
      name: 'Analysis: Refine Results Started',
      properties: ['Report ID', 'Total Rows', 'Selected Rows Count', 'Selected Tab'] as const,
    },
    refineResultsCompleted: {
      name: 'Analysis: Refine Results Completed',
      properties: ['Report ID', 'Total Rows', 'Selected Rows Count', 'Change in Selected Rows'] as const,
    },
    displayOptionChanged: {
      name: 'Analysis: Display Option Changed',
      properties: [
        'Report ID',
        'Edit Type',
        'Selected Tab',
        'Metrics',
        'Channels',
        'Table Sorted By',
        'Table Sort Order',
        'Chart Type',
        'Chart Unit',
        'Chart Grouping',
        'Chart Metric',
        'Chart Selected Lines Count',
      ] as const,
    },
    chartHover: {
      name: 'Analysis: Chart Hover',
      properties: [
        'Report ID',
        'Chart Type',
        'Chart Unit',
        'Chart Grouping',
        'Chart Metric',
        'Chart Selected Lines Count',
        'Time of Hover',
      ] as const,
    },
  },
  tags: {
    createStarted: {
      name: 'Tags: Create Started',
      properties: [] as const,
    },
    createCanceled: {
      name: 'Tags: Create Canceled',
      properties: [] as const,
    },
    deleteStarted: {
      name: 'Tags: Delete Started',
      properties: [
        'Tag ID',
        'Tag Name',
        'Campaign Usage Count',
        'Newsletter Usage Count',
        'Broadcast Usage Count',
        'Segment Usage Count',
      ] as const,
    },
    editStarted: {
      name: 'Tags: Edit Started',
      properties: [
        'Tag ID',
        'Tag Name',
        'Campaign Usage Count',
        'Newsletter Usage Count',
        'Broadcast Usage Count',
        'Segment Usage Count',
      ] as const,
    },
    manageStarted: {
      name: 'Tags: Manage Started',
      properties: [
        'Tag Count Total',
        'Campaign Usage Count Total',
        'Newsletter Usage Count Total',
        'Broadcast Usage Count Total',
        'Segment Usage Count Total',
        'Tag Names',
      ] as const,
    },
    manageCompleted: {
      name: 'Tags: Manage Completed',
      properties: [
        'Tag Count Total',
        'Campaign Usage Count Total',
        'Newsletter Usage Count Total',
        'Broadcast Usage Count Total',
        'Segment Usage Count Total',
        'Tag Names',
      ] as const,
    },
  },
  // Client-side *intent* events only — lifecycle events (created/renamed/
  // deleted/imported) are emitted by the backend (ui_api MixTrack), which is
  // authoritative and covers non-dashboard paths. Firing both would double
  // count. (MBL-1940)
  // The LLM step's starting-point picker, in its panel.
  llm: {
    onboardingCategorySelected: {
      name: 'LLM Action: Onboarding Category Selected',
      properties: ['Onboarding Category'] as const,
    },
    // The category the step's name says it was started from, chosen for the person.
    onboardingCategoryPreselected: {
      name: 'LLM Action: Onboarding Category Preselected',
      properties: ['Onboarding Category'] as const,
    },
    onboardingPresetSelected: {
      name: 'LLM Action: Onboarding Preset Selected',
      properties: ['Onboarding Category', 'Onboarding Preset'] as const,
    },
    onboardingCustomSelected: {
      name: 'LLM Action: Onboarding Custom Selected',
      properties: [] as const,
    },
    // The guided setup's funnel. Every event inside the workspace says whether it refined a set-up step.
    // How often the describe box is in front of someone, set up or not.
    guidedSetupViewed: {
      name: 'LLM Action: Guided Setup Viewed',
      properties: ['Is Configured'] as const,
    },
    guidedSetupSeedSelected: {
      name: 'LLM Action: Guided Setup Seed Selected',
      properties: ['Seed'] as const,
    },
    guidedSetupAdvancedEditorOpened: {
      name: 'LLM Action: Guided Setup Advanced Editor Opened',
      properties: ['Is Resuming'] as const,
    },
    guidedSetupStarted: {
      name: 'LLM Action: Guided Setup Started',
      properties: ['Is Resuming', 'Input Length', 'Used Seed'] as const,
    },
    guidedSetupRefused: {
      name: 'LLM Action: Guided Setup Refused',
      properties: ['Is Resuming', 'Reason'] as const,
    },
    guidedSetupSetupCompleted: {
      name: 'LLM Action: Guided Setup Setup Completed',
      properties: [
        'Is Resuming',
        'Duration Ms',
        'Field Count',
        'Source Count',
        'Library Entry Count',
        'Did Run',
      ] as const,
    },
    guidedSetupSetupFailed: {
      name: 'LLM Action: Guided Setup Setup Failed',
      properties: ['Is Resuming', 'Step', 'Reason'] as const,
    },
    guidedSetupRun: {
      name: 'LLM Action: Guided Setup Run',
      properties: ['Is Resuming', 'Trigger', 'Model', 'Succeeded', 'Error'] as const,
    },
    guidedSetupRefined: {
      name: 'LLM Action: Guided Setup Refined',
      properties: [
        'Is Resuming',
        'Source',
        'Chip',
        'Input Length',
        'Refine Count',
        'Succeeded',
        'Did Change Fields',
      ] as const,
    },
    guidedSetupPromptEdited: {
      name: 'LLM Action: Guided Setup Prompt Edited',
      properties: ['Is Resuming'] as const,
    },
    guidedSetupSourceRemoved: {
      name: 'LLM Action: Guided Setup Source Removed',
      properties: ['Is Resuming', 'Was Suggested'] as const,
    },
    guidedSetupProfileOpened: {
      name: 'LLM Action: Guided Setup Profile Opened',
      properties: ['Is Resuming'] as const,
    },
    guidedSetupClosed: {
      name: 'LLM Action: Guided Setup Closed',
      properties: ['Is Resuming', 'Stage', 'Duration Ms', 'Refine Count', 'Did Run'] as const,
    },
  },
  geofences: {
    geosetCreateStarted: {
      name: 'Geofences: Geoset Create Started',
      properties: ['Entry Point', 'Plan Tier'] as const,
    },
    geofenceCreateStarted: {
      name: 'Geofences: Geofence Create Started',
      properties: ['Entry Point', 'Plan Tier', 'Geoset ID'] as const,
    },
    geofenceDeleteStarted: {
      name: 'Geofences: Geofence Delete Started',
      properties: ['Plan Tier', 'Geoset ID', 'Geofence ID', 'Geofence Name'] as const,
    },
    // Both import flows share one wire name — 'Entry Point' tells them apart —
    // but are separate registry entries because they send different property
    // shapes and the registry requires every listed property from every caller.
    geofenceImportStarted: {
      name: 'Geofences: Import Started',
      properties: [
        'Entry Point',
        'Plan Tier',
        'Geoset ID',
        'File Name',
        'File Size Bytes',
        'Import Mode',
        'File Format',
        'Valid Row Count',
        'Invalid Row Count',
      ] as const,
    },
    // No 'Target New Geoset': the import flow is choose-existing-only
    // (MBL-2119) — new sets are always created from the create flow first.
    geosetImportStarted: {
      name: 'Geofences: Import Started',
      properties: ['Entry Point', 'Plan Tier', 'Geoset ID', 'File Name', 'File Size Bytes', 'Import Mode'] as const,
    },
  },
  // Design Studio onboarding events, ported from parcel's analytics catalog
  // (`app/src/services/analytics/types.ts`) for the welcome-screen port
  // (CON-7496). Only the events the ported welcome screen emits are carried
  // over; the names match parcel so the pipeline sees identical events.
  onboarding: {
    welcomePageClicked: {
      name: 'Design Studio: Welcome Page Clicked',
      properties: ['choice'] as const,
    },
    started: {
      name: 'Design Studio: Onboarding Started',
      properties: [] as const,
    },
    pricingCardButtonClicked: {
      name: 'Onboarding: Pricing Card Button Clicked',
      properties: ['Button Name'] as const,
    },
  },
  inline_contracts_sales: {
    formOpened: {
      name: 'Inline Contracts Sales Form: Opened',
      properties: [
        'Account Id',
        'Requester Name',
        'Requester Email',
        'Requester User Id',
        'Current Plan',
        'Requested Plan',
      ] as const,
    },
    formClosed: {
      name: 'Inline Contracts Sales Form: Closed',
      properties: [
        'Account Id',
        'Requester Name',
        'Requester Email',
        'Requester User Id',
        'Current Plan',
        'Requested Plan',
      ] as const,
    },
    formCancelClicked: {
      name: 'Inline Contracts Sales Form: Cancel Clicked',
      properties: [
        'Account Id',
        'Requester Name',
        'Requester Email',
        'Requester User Id',
        'Current Plan',
        'Requested Plan',
      ] as const,
    },
    formCancelDismissed: {
      name: 'Inline Contracts Sales Form: Cancel Dismissed',
      properties: [
        'Account Id',
        'Requester Name',
        'Requester Email',
        'Requester User Id',
        'Current Plan',
        'Requested Plan',
      ] as const,
    },
    formFieldInput: {
      name: 'Inline Contracts Sales Form: Field Input',
      properties: [
        'Account Id',
        'Requester Name',
        'Requester Email',
        'Requester User Id',
        'Current Plan',
        'Requested Plan',
        'Field Name',
      ] as const,
    },
    formSubmitClicked: {
      name: 'Inline Contracts Sales Form: Submit Clicked',
      properties: [
        'Account Id',
        'Requester Name',
        'Requester Email',
        'Requester User Id',
        'Current Plan',
        'Requested Plan',
      ] as const,
    },
    formSubmitError: {
      name: 'Inline Contracts Sales Form: Submit Error',
      properties: [
        'Account Id',
        'Requester Name',
        'Requester Email',
        'Requester User Id',
        'Current Plan',
        'Requested Plan',
      ] as const,
    },
    formSubmitSuccess: {
      name: 'Inline Contracts Sales Form: Submit Success',
      properties: [
        'Account Id',
        'Requester Name',
        'Requester Email',
        'Requester User Id',
        'Current Plan',
        'Requested Plan',
      ] as const,
    },
  },
  // Design Studio dashboard events, ported from parcel's analytics catalog
  // (`app/src/services/analytics/types.ts`) for the dashboard port
  // (CON-7496). Parcel posts these to the same track_event endpoint; only
  // the events the ported hooks emit are carried over.
  dashboard: {
    filetreeAction: {
      name: 'Design Studio: Filetree Action',
      properties: ['action_type', 'filetree_item', 'current_view'] as const,
    },
    changedFilesView: {
      name: 'Design Studio: Changed Files View',
      properties: ['current_view'] as const,
    },
    filetreeSorted: {
      name: 'Design Studio: Filetree Sorted',
      properties: ['sort_column', 'sort_direction', 'current_view'] as const,
    },
  },
  // Design Studio bulk-publish event, ported from parcel's analytics catalog
  // (`app/src/services/analytics/types.ts`) for the dashboard bulk-publish port
  // (CON-7507). Name matches parcel so the pipeline sees identical events.
  bulkPublish: {
    completed: {
      name: 'Design Studio: Bulk Publish Completed',
      properties: ['source', 'message_count', 'successful_count', 'failed_count'] as const,
    },
  },
  folder: {
    created: {
      name: 'Design Studio: Folder Created',
      properties: ['id', 'path'] as const,
    },
    // Parcel's `folder.deleted`, fired by the dashboard delete flow (CON-7986).
    deleted: {
      name: 'Design Studio: Folder Deleted',
      properties: ['id', 'path'] as const,
    },
  },
  // Design Studio created-events, ported from parcel's analytics catalog.
  // Parcel fires these from its Overmind nodes store
  // (`app/src/features/workspace-page/stores/nodes.ts`) on create and
  // duplicate; the dashboard port re-fires them from the create modals and
  // the duplicate hook. Names match parcel so the pipeline sees identical
  // events.
  component: {
    created: {
      name: 'Design Studio: Component Created',
      properties: ['id', 'path'] as const,
    },
    // Parcel's `component.deleted`, fired by the dashboard delete flow (CON-7986).
    deleted: {
      name: 'Design Studio: Component Deleted',
      properties: ['id', 'path'] as const,
    },
  },
  whatsapp: {
    templateCreated: {
      name: 'Design Studio: WhatsApp: Template Created',
      properties: ['id', 'Editor', 'category', 'language'] as const,
    },
    // WhatsApp channel settings: Embedded Signup was opened, and the
    // connection it finished with was saved or not.
    setupStart: {
      name: 'WhatsApp: Setup Started',
      properties: [] as const,
    },
    setupSuccess: {
      name: 'WhatsApp: Setup Completed',
      properties: [] as const,
    },
    setupFail: {
      name: 'WhatsApp: Setup Failed',
      properties: [] as const,
    },
  },
  userFeedback: {
    clicked: {
      name: 'Design Studio: User Feedback Clicked',
      properties: ['label', 'metadata', 'journeys_workspace_id', 'journeys_template_id'] as const,
    },
    // Parcel's `userFeedback.submitted` — parcel fired it from its local
    // feedback modal; hydra's shared feedback modal is the submit path now,
    // so tracked feedback buttons fire it on successful submit (CON-7986).
    submitted: {
      name: 'Design Studio: User Feedback Submitted',
      properties: ['label', 'metadata', 'feedback', 'journeys_workspace_id', 'journeys_template_id'] as const,
    },
  },
  // Mirrored verbatim (names + payloads) from parcel's `globalStyles` analytics
  // namespace (`app/src/services/analytics/types.ts`) for the global-styles
  // stores slice (CON-7595). Names match parcel so the pipeline sees identical
  // events. `variantEventProps` (CON-7225) emits the `Variant Type` /
  // `Is Default Variant` / `Variant Count For Variable` dimensions.
  globalStyles: {
    viewed: {
      name: 'Design Studio: Global Styles: Viewed',
      properties: [
        'color_variable_count',
        'font_variable_count',
        'radius_variable_count',
        'spacing_variable_count',
        'text_style_variable_count',
      ] as const,
    },
    addStarted: {
      name: 'Design Studio: Global Styles: Add Started',
      properties: ['variable_type'] as const,
    },
    addCompleted: {
      name: 'Design Studio: Global Styles: Add Completed',
      properties: [
        'variable_type',
        'variable_name',
        'value',
        'Variant Type',
        'Is Default Variant',
        'Variant Count For Variable',
      ] as const,
    },
    updateStarted: {
      name: 'Design Studio: Global Styles: Update Started',
      properties: ['variable_type'] as const,
    },
    updateCompleted: {
      name: 'Design Studio: Global Styles: Update Completed',
      properties: [
        'variable_type',
        'variable_name',
        'value',
        'update_type',
        'Variant Type',
        'Is Default Variant',
        'Variant Count For Variable',
      ] as const,
    },
    saved: {
      name: 'Design Studio: Global Styles: Saved',
      properties: [
        'color_variable_count',
        'font_variable_count',
        'radius_variable_count',
        'spacing_variable_count',
        'text_style_variable_count',
      ] as const,
    },
    variableDeleteStarted: {
      name: 'Design Studio: Global Styles: Variable Delete Started',
      properties: ['variable_type', 'variable_name', 'value'] as const,
    },
    variableDeleteCompleted: {
      name: 'Design Studio: Global Styles: Variable Delete Completed',
      properties: ['variable_type', 'variable_name', 'value', 'Variant Type'] as const,
    },
    discarded: {
      name: 'Design Studio: Global Styles: Discarded',
      properties: [] as const,
    },
  },
  // Mirrored verbatim (names + payloads) from parcel's `aiContext` analytics
  // namespace (`app/src/services/analytics/types.ts`) for the AI-context
  // extraction-flows slice (CON-7595, slice 6). Names match parcel so the
  // pipeline sees identical events. The other parcel `aiContext` consumers
  // (visual editor, builder-start-modal) stay in parcel; only the events the
  // ported global-styles surfaces emit are carried over.
  aiContext: {
    introBannerDismissed: {
      name: 'Design Studio: AI Context: Intro Banner Dismissed',
      properties: [] as const,
    },
    styleGenerationStarted: {
      name: 'Design Studio: AI Context: Style Generation Started',
      properties: [] as const,
    },
    errorBannerDismissed: {
      name: 'Design Studio: AI Context: Error Banner Dismissed',
      properties: [] as const,
    },
    successBannerDismissed: {
      name: 'Design Studio: AI Context: Success Banner Dismissed',
      properties: [] as const,
    },
  },
  // Brand document generation funnel (Global Styles page → wizard). Run-scoped
  // events share `run_id` (the extract run id, also sent to the server) so
  // client and server events join per run.
  brand: {
    pageViewed: {
      name: 'Design Studio: Brand: Page Viewed',
      properties: ['has_document', 'can_generate', 'blocked_reason', 'run_state'] as const,
    },
    introBannerDismissed: {
      name: 'Design Studio: Brand: Intro Banner Dismissed',
      properties: [] as const,
    },
    generationStarted: {
      name: 'Design Studio: Brand: Generation Started',
      properties: [...BrandRunProperties, 'replaced_live_run'] as const,
    },
    extractionCompleted: {
      name: 'Design Studio: Brand: Extraction Completed',
      properties: [
        ...BrandRunProperties,
        'source_count',
        'color_count',
        'font_count',
        'radius_count',
        'spacing_count',
        'button_count',
        'text_style_count',
        'link_count',
        'divider_count',
      ] as const,
    },
    stylesReviewed: {
      name: 'Design Studio: Brand: Styles Reviewed',
      properties: [
        ...BrandRunProperties,
        'outcome',
        'total_count',
        'total_new_count',
        'total_overwrite_count',
        'selected_count',
        'selected_new_count',
        'selected_overwrite_count',
        'selected_color_count',
        'selected_font_count',
        'selected_radius_count',
        'selected_spacing_count',
        'selected_button_count',
        'selected_text_style_count',
        'selected_link_count',
        'selected_divider_count',
      ] as const,
    },
    authoringLeftInBackground: {
      name: 'Design Studio: Brand: Authoring Left In Background',
      properties: [...BrandRunProperties, 'phase', 'source'] as const,
    },
    runResumed: {
      name: 'Design Studio: Brand: Run Resumed',
      properties: [...BrandRunProperties, 'source', 'phase_at_resume', 'away_ms'] as const,
    },
    documentReviewed: {
      name: 'Design Studio: Brand: Document Reviewed',
      properties: [
        ...BrandRunProperties,
        'backgrounded',
        'authoring_ms',
        'document_bytes',
        'section_count',
        'staged_styles',
        'asset_import_outcome',
        'assets_imported_count',
        'assets_skipped_count',
      ] as const,
    },
    documentAccepted: {
      name: 'Design Studio: Brand: Document Accepted',
      properties: [
        ...BrandRunProperties,
        'saved_styles',
        'selected_count',
        'selected_overwrite_count',
        'assets_imported_count',
        'document_bytes',
        'section_count',
        'backgrounded',
        'resume_count',
      ] as const,
    },
    generationRetried: {
      name: 'Design Studio: Brand: Generation Retried',
      properties: [...BrandRunProperties, 'from', 'attempt'] as const,
    },
    generationCancelled: {
      name: 'Design Studio: Brand: Generation Cancelled',
      properties: [...BrandRunProperties, 'phase', 'source', 'backgrounded'] as const,
    },
    documentExported: {
      name: 'Design Studio: Brand: Document Exported',
      properties: ['has_document', 'file_count'] as const,
    },
  },
  // Object-type settings events (FPS-2357). Only `listViewed` fires from the
  // index page; the create/edit-started and business-type events fire from the
  // new/edit flows shipped in follow-up PRs. `businessTypeSelfSelected` keeps the
  // 'User:' wire-name prefix the Ember catalog used.
  objects: {
    listViewed: {
      name: 'Object Type: List Viewed',
      properties: ['Has Enabled Objects', 'Has Objects Data'] as const,
    },
    predefinedListViewed: {
      name: 'Object Type: Predefined List Viewed',
      properties: ['Has Enabled Objects', 'Has Objects Data'] as const,
    },
    createStarted: {
      name: 'Object Type: Create Started',
      properties: [] as const,
    },
    editStarted: {
      name: 'Object Type: Edit Started',
      properties: [] as const,
    },
    apiExampleCopied: {
      name: 'Object Type: Copy API Request Clicked',
      properties: [] as const,
    },
    objectCreateStarted: {
      name: 'Object: Create Started',
      properties: [] as const,
    },
    // The trailing space is part of the name the destination already receives;
    // trimming it would split the funnel into two events.
    // Fired from the object profile's relationships table. 'Number of People' is
    // the size of the row selection and 'Number of Objects' is always 1 — the
    // table deletes relationships for one object at a time.
    relationshipsManualDeleteCompleted: {
      name: 'Object: Relationship Manually Deleted',
      properties: ['Source', 'Number of People', 'Number of Objects'] as const,
    },
    objectNewAttributeCreateClicked: {
      name: 'Object: New Attribute Create Clicked ',
      properties: ['cio_object_id', 'object_id'] as const,
    },
    // The object profile's tab views, fired once per visit to each tab (Ember fired them on the
    // route transition, `generate-transition-event.ts`).
    objectOverviewTabViewed: {
      name: 'Object: Overview Tab Viewed',
      properties: ['cio_object_id', 'object_id'] as const,
    },
    objectAttributesTabViewed: {
      name: 'Object: Attributes Tab Viewed',
      properties: ['cio_object_id', 'object_id'] as const,
    },
    objectRelationshipTabViewed: {
      name: 'Object: Relationships Tab Viewed',
      properties: ['cio_object_id', 'object_id'] as const,
    },
    objectActivityTabViewed: {
      name: 'Object: Activity Tab Viewed',
      properties: ['cio_object_id', 'object_id'] as const,
    },
    objectDisplayedAttributeEditStarted: {
      name: 'Object: Displayed Attribute Edit Started',
      properties: ['cio_object_id', 'object_id'] as const,
    },
    guidePageLinkCopied: {
      name: 'Object Type: Copy Page Link Clicked',
      properties: [] as const,
    },
    businessTypeSelfSelected: {
      name: 'User: Business Type Self-Selected',
      properties: ['Business Type'] as const,
    },
    listViewedSearchedFiltered: {
      name: 'Object: List Viewed, Searched or Filtered',
      properties: [
        'Filter Operator',
        'Number of Object Attribute Conditions',
        'Number of Relationship Attribute Conditions',
        'Has Enabled Objects',
        'Has Objects Data',
        'Filtered by Name',
        'Name Filter Includes Wildcard',
        'Filtered by Without Relationships',
        'Filtered by With Relationships',
      ] as const,
    },
    relationshipsManualAddStarted: {
      name: 'Object: Relationship Manual Add Started',
      properties: ['Source'] as const,
    },
    relationshipsManualAddCompleted: {
      name: 'Object: Relationship Manual Add Completed',
      properties: ['Source', 'Number of People', 'Number of Objects', 'Number of Relationship Attributes'] as const,
    },
  },
  // 'Provider' is an `SmsProviderId` ('infobip' | 'sinch' | 'twilio' | 'vonage' | 'bird').
  // Saved fires on the save submission, not on a successful connection — the two
  // counts differ by credentials the provider rejected.
  sms: {
    providerConnectStarted: {
      name: 'SMS: Provider Connect Started',
      properties: ['Provider'] as const,
    },
    providerConnectSaved: {
      name: 'SMS: Provider Connect Saved',
      properties: ['Provider'] as const,
    },
    // Design Studio SMS create, ported from parcel's catalog — parcel fires it
    // from its `create-sms-modal`; the dashboard port fires it from hydra's.
    templateCreated: {
      name: 'Design Studio: SMS: Template Created',
      properties: ['id', 'Editor'] as const,
    },
  },
  // Outbox delivery retry funnel (Ember `message` category `retry_send_*`). Started
  // fires when a bulk retry opens its confirm; Confirmed/Canceled on the outcome. A
  // per-row retry has no confirm step, so it fires only Confirmed. `Override Message
  // Limit` is always false here — the messaging-limit override modal isn't ported.
  message: {
    errorsViewed: {
      name: 'Message: Errors Viewed',
      properties: [
        'Message Used In',
        'Newsletter Content ID',
        'Newsletter ID',
        'Language',
        'Language Count',
        'Builder Type',
        'Error Content',
      ],
    },
    errorsResearched: {
      name: 'Message: Errors Researched',
      properties: [
        'Message Used In',
        'Newsletter Content ID',
        'Newsletter ID',
        'Language',
        'Language Count',
        'Builder Type',
        'Error Content',
      ],
    },
    retrySendStarted: {
      name: 'Message: Retry Send Started',
      properties: ['Selected Message Count', 'Selected Statuses'] as const,
    },
    retrySendConfirmed: {
      name: 'Message: Retry Send Confirmed',
      properties: ['Selected Message Count', 'Selected Statuses', 'Override Message Limit'] as const,
    },
    retrySendCanceled: {
      name: 'Message: Retry Send Canceled',
      properties: ['Selected Message Count', 'Selected Statuses'] as const,
    },
    // The URL-parameters edit page. Ember fired this per transition into
    // `actions.url_params.edit`; the migrated page fires it on page view instead.
    urlParamsEditStarted: {
      name: 'Message: URL Parameters Edit Started',
      properties: [] as const,
    },
    // The time-zone-match test tool; fires once per lookup attempt, success
    // and failure alike. Name and (empty) properties match the Ember catalog.
    timeZoneAttributeTested: {
      name: 'Message: Time Zone Attribute Tested',
      properties: [] as const,
    },
    // The in-app settings page's test message: started when its send button
    // is clicked, successful once the test is reported delivered.
    sendTestStarted: {
      name: 'Message: Send Test Started',
      properties: ['Message Type', 'Message Used In'] as const,
    },
    sendTestSuccessful: {
      name: 'Message: Send Test Successful',
      properties: ['Message Type', 'Message Used In'] as const,
    },
  },
  // Activity Logs page events. Names and properties match the Ember catalog
  // (`ui/app/utils/analytics/events.ts` + `transforms/logs.ts`) so dashboards
  // stay contiguous across the migration (FPS-2304).
  sampleWorkspace: {
    // The welcome empty state's "View sample workspace" button. 'Route Name' carries the
    // embedded app's name for the page the button sat on, which is what this event has always
    // reported, so a chart spanning both apps reads one series.
    viewSampleWorkspaceClicked: {
      name: 'Sample Workspace: View Sample Workspace Clicked',
      properties: ['Route Name'] as const,
    },
  },
  logs: {
    viewed: {
      name: 'Log: Activity Log Viewed',
      properties: ['Activity Tab'] as const,
    },
    // The data index's own view event, fired per browser rather than per
    // navigation: the generic page view cannot say which browser was opened
    // or whether a search was narrowing it.
    dataIndexViewed: {
      name: 'Log: Data Index Viewed',
      properties: ['Data Type', 'Filtered By Name'] as const,
    },
    // One item's own page. The page this replaces reports an attribute and
    // everything else separately, and reads "everything else" off the URL
    // segment — so an object attribute reports as an event, which is odd but
    // is what the funnels already count.
    dataIndexAttributeViewed: {
      name: 'Log: Data Index Attribute Viewed',
      properties: [] as const,
    },
    dataIndexEventViewed: {
      name: 'Log: Data Index Event Viewed',
      properties: [] as const,
    },
    moreLogsLoaded: {
      name: 'Log: More Logs Loaded',
      properties: ['Activity Tab'] as const,
    },
    specificActivityViewed: {
      name: 'Log: Activity Log Specific Activity Viewed',
      properties: ['Activity Tab', 'Merged Anonymous Event', 'Merge Activity Type'] as const,
    },
    // The shared welcome empty state fires these on every surface that renders it; 'Empty State'
    // carries the surface's label (e.g. "People" / "Profiles").
    emptyStateViewed: {
      name: 'Log: Empty State Viewed',
      properties: ['Empty State'] as const,
    },
    emptyStateActionClicked: {
      name: 'Log: Empty State Action Clicked',
      properties: ['Empty State'] as const,
    },
  },
  people: {
    listViewedSearchedFiltered: {
      name: 'People: List Viewed, searched, or filtered',
      properties: [
        'Filter Operator',
        'Filtered by Email Address',
        'Email Filter Includes Wildcard',
        'Number of Segment Conditions',
        'Number of Attribute Conditions',
        'Number of Newsletter Conditions',
        'Number of Message Conditions',
        'Number of Event Conditions',
        'Number of Relationship Attribute Conditions',
      ] as const,
      optionalProperties: ['Source'] as const,
    },
    emptyStateViewed: {
      name: 'People: Empty State Viewed',
      properties: [] as const,
    },
    addPeopleModalViewed: {
      name: 'People: Add People Modal Viewed',
      properties: [] as const,
    },
    addPeopleActionClicked: {
      name: 'People: Add People Action Clicked',
      properties: ['Action Name', 'Page Name'] as const,
    },
    manualPersonCreationStarted: {
      name: 'People: Manual Person Creation Started',
      properties: [] as const,
    },
    mergeSetupTabViewed: {
      name: 'Merge: Setup Tab Viewed',
      properties: ['Source', 'Primary Profile Selected', 'Secondary Profile Selected'] as const,
    },
    mergeReviewAndConfirmTabViewed: {
      name: 'Merge: Review and Confirm Tab Viewed',
      properties: [
        'Primary Attribute Count',
        'Secondary Attribute Count (Added)',
        'Secondary Attribute Count (Lost)',
      ] as const,
    },
    // The attribute-condition properties are sent only for a condition search.
    mergePersonSearched: {
      name: 'Merge: Person Searched',
      properties: [
        'Filtered by Email Address',
        'Filtered by Profile ID',
        'Filtered by Attribute Condition',
        'More than Ten Matches',
      ] as const,
      optionalProperties: ['Attribute Name', 'Attribute Filter Operator', 'Attribute Value'] as const,
    },
    mergePersonSelected: {
      name: 'Merge: Person Selected',
      properties: ['Profile Type'] as const,
    },
    mergePersonCleared: {
      name: 'Merge: Person Cleared',
      properties: ['Profile Type'] as const,
    },
    mergePeopleSwapped: {
      name: 'Merge: People Swapped',
      properties: [] as const,
    },
    mergeAttributesSearched: {
      name: 'Merge: Attributes Searched',
      properties: ['Source', 'Attribute Search Value'] as const,
    },
    mergeFeedbackRequestStarted: {
      name: 'Merge: Feedback Request Started',
      properties: ['Source', 'Feedback Type'] as const,
    },
  },
  subscriptionCenter: {
    viewed: {
      name: 'Subscription Center: Page Viewed',
      properties: [] as const,
    },
    addTopicStarted: {
      name: 'Subscription Center: Add Topic Started',
      properties: [] as const,
    },
    editTopicStarted: {
      name: 'Subscription Center: Edit Topic Started',
      properties: [] as const,
    },
    deleteTopicStarted: {
      name: 'Subscription Center: Delete Topic Started',
      properties: [] as const,
    },
    deleteChannelStarted: {
      name: 'Subscription Center: Delete Channel Started',
      properties: [] as const,
    },
    previewClicked: {
      name: 'Subscription Center: Preview Page Clicked',
      properties: [] as const,
    },
    setBulkPreferencesStarted: {
      name: 'Subscription Center: Set Bulk Preferences Started',
      properties: [] as const,
    },
    // `Filter` is the comma-separated list of filters that were set, e.g.
    // 'Type, Tags'. Ember built the same string in its analytics transform.
    setBulkPreferencesFiltered: {
      name: 'Subscription Center: Set Bulk Preferences Filtered',
      properties: ['Filter'] as const,
    },
    setBulkPreferencesSubmitted: {
      name: 'Subscription Center: Set Bulk Preferences Submitted',
      properties: [] as const,
    },
    activationCompleted: {
      name: 'Subscription Center: Activation Completed',
      properties: [] as const,
    },
    deactivationCompleted: {
      name: 'Subscription Center: Deactivation Completed',
      properties: [] as const,
    },
    settingsPageViewed: {
      name: 'Subscription Center: Settings Page Viewed',
      properties: [] as const,
    },
    addLanguagesStarted: {
      name: 'Subscription Center: Add Languages Started',
      properties: [] as const,
    },
    editLanguagesStarted: {
      name: 'Subscription Center: Edit Languages Started',
      properties: [] as const,
    },
    // `Edition` is 'delete', 'make live' or 'make not live'. Ember built the
    // property name in an analytics transform; here the caller passes it.
    editLanguagesSubmitted: {
      name: 'Subscription Center: Edit languages submitted',
      properties: ['Edition'] as const,
    },
    languageEditStarted: {
      name: 'Subscription Center: Language edit started',
      properties: [] as const,
    },
    languageEdited: {
      name: 'Subscription Center: Language edited',
      properties: [] as const,
    },
    languageDeleted: {
      name: 'Subscription Center: Language deleted',
      properties: [] as const,
    },
    // `Status` is 'live' or 'not live', which is how Ember's transform reported
    // the boolean the toggle wrote.
    languageStatusChanged: {
      name: 'Subscription Center: Language status changed',
      properties: ['Status'] as const,
    },
    brandingPageViewed: {
      name: 'Subscription Center: Branding Page Viewed',
      properties: [] as const,
    },
    brandingUpdated: {
      name: 'Subscription Center: Branding Updated',
      properties: [] as const,
    },
    // Fires on every exit from the branding editor, saved or not, despite the
    // name the event was registered under.
    brandingExited: {
      name: 'Subscription Center: Branding Exited Without Changes',
      properties: [] as const,
    },
  },
  // The classic dashboard (`/dashboard`). Names and properties match the Ember
  // catalog (`ui/app/utils/analytics/events.ts` + `transforms/dashboard.ts`) so
  // the funnels stay contiguous across the migration (FPS-2301). The namespace
  // is spelled out because `dashboard` above is Design Studio's.
  classicDashboard: {
    // Fired on every entry, and again when the window in the URL changes: the
    // page this replaces is not on the transition tracker's exact-match list,
    // so a query-param transition counts as a new view there too.
    viewed: {
      name: 'Dashboard: Viewed',
      properties: [] as const,
    },
    leaderboardCardDisplayChanged: {
      name: 'Dashboard: Leaderboard Card Display Changed',
      properties: ['Selected Tab', 'Sort Direction', 'Edit Type'] as const,
    },
    testsCardDisplayChanged: {
      name: 'Dashboard: Tests Card Display Changed',
      properties: ['Selected Tab', 'Sort By', 'Edit Type'] as const,
    },
    // The segment properties stay in the shape because the Segments card fires
    // this event too, and a funnel reading both wants one shape.
    chartChanged: {
      name: 'Dashboard: Chart Changed',
      properties: ['Card', 'Edit Type', 'Segment ID', 'Segment Name', 'Metric', 'Channel'] as const,
    },
    navigateAway: {
      name: 'Dashboard: Navigate Away',
      properties: ['Page View Time', 'Destination', 'Card', 'Channel', 'Metric', 'Sort Order'] as const,
    },
    // No `Group By Value`: the page this replaces reads its period-keyed label
    // map with a grouping value, so the property resolves to nothing and has
    // never reached the funnel. Populating it would be a new event shape.
    timeframeFiltersChanged: {
      name: 'Dashboard: Timeframe Filters Changed',
      properties: ['Filtered Timeframe', 'Filtered Timeframe Length', 'Edit Type'] as const,
    },
  },
  // Connecting a form to the workspace. Wire names and property keys are the
  // ones the forms funnels already read, so both surfaces report as one.
  forms: {
    settings_viewed: {
      name: 'Forms: Settings Viewed',
      properties: [] as const,
    },
    list_viewed_or_filtered: {
      name: 'Forms: List Viewed or Filtered',
      properties: ['Filtered To'],
    },
    feedback_request_started: {
      name: 'Forms: Feedback Request Started',
      properties: [],
    },
    manage_form_started: {
      name: 'Forms: Manage Form Started',
      properties: ['environmentId', 'action', 'formType', 'formId', 'api', 'scanned'],
    },
    // Fired when the grid of form builders comes on screen, wherever it is
    // embedded: the forms page, and the campaign trigger's fly-out.
    formBuildersViewed: {
      name: 'Forms: Form Builders Viewed',
      properties: [] as const,
    },
    // A builder's card was pressed, which leaves for that builder's docs.
    formBuilderClicked: {
      name: 'Forms: Form Builder Clicked',
      properties: ['Integration Name'] as const,
    },
    // The steps of setting up a scanned form, each fired when it comes on
    // screen. `Source` names the surface the setup was opened from.
    setupFindFormTabViewed: {
      name: 'Forms: Find Form Tab Viewed',
      properties: ['Source', 'Re-scan'] as const,
    },
    // `Number of Form Fields` is a list: one count per form the scan found.
    setupSelectFormTabViewed: {
      name: 'Forms: Select Form Tab Viewed',
      properties: ['Source', 'Forms Detected', 'Number of Form Fields'] as const,
    },
    // The step of setting up a form that maps its questions onto profile
    // attributes came on screen. `Source` names the surface the setup began on.
    mapFormFieldsTabViewed: {
      name: 'Forms: Map Form Fields Tab Viewed',
      properties: ['Source'] as const,
    },
    // One of the ways to send a form's submissions came on screen: the forms
    // endpoint, the HTML action attribute, or the JavaScript snippet.
    codeViewed: {
      name: 'Forms: Code Viewed',
      properties: ['Code Displayed', 'Source'] as const,
    },
    // A URL or code sample for sending a form's submissions was copied.
    // `Language` is sent only for the forms endpoint's cURL sample.
    codeCopied: {
      name: 'Forms: Code Copied',
      properties: ['Content Copied', 'Source'] as const,
      optionalProperties: ['Language'] as const,
    },
  },
  // Connecting a Facebook business page and its lead forms. The wire names are
  // the ones the Facebook Lead Ads funnel already reads. `Source` names the
  // surface the connection began on.
  facebookLeadAds: {
    connectAccountStarted: {
      name: 'FB Lead Ads: Connect Account Started',
      properties: ['Source'] as const,
    },
    // The Facebook sign-in came back with every permission the lead forms need.
    connectAccountCompleted: {
      name: 'FB Lead Ads: Connect Account Completed',
      properties: [] as const,
    },
    // The choice of which business page to connect came on screen.
    connectBusinessPageStarted: {
      name: 'FB Lead Ads: Connect Business Page Started',
      properties: [] as const,
    },
    disconnectBusinessPageStarted: {
      name: 'FB Lead Ads: Disconnect Business Page Started',
      properties: [] as const,
    },
    // Conversion tracking was switched on, which is a request for a feature we
    // do not have yet: it opens a feedback form rather than a setting.
    // `Source` is sent only from the surfaces the funnel names: the forms page
    // and the campaign goal step.
    conversionEnabled: {
      name: 'FB Lead Ads: Conversion Enabled',
      properties: [] as const,
      optionalProperties: ['Source'] as const,
    },
  },
  // Creating a data-driven segment. `Source` names the surface the segment was
  // created from, such as 'Campaign Trigger'.
  segment: {
    // The inline create-segment form opened from a segment picker.
    creationStarted: {
      name: 'Segment: Creation Started',
      properties: ['Source'] as const,
    },
    created: {
      name: 'Segment: Created',
      properties: ['Source', 'AI Assisted'] as const,
    },
    // An edit to a JSON attribute path in a condition row: a level added or
    // deleted, or the path or its comparison edited. 'JSON Interface Used' is
    // 'Basic' or 'Advanced', 'JSON Condition Edit Type' is 'Added', 'Deleted'
    // or 'Edited'.
    jsonConditionEdited: {
      name: 'Segment: JSON Condition Edited',
      properties: ['JSON Interface Used', 'JSON Condition Edit Type'] as const,
    },
  },
  audience: {
    add_network_started: {
      name: 'Audience: Add Network Started',
      properties: ['Ad Network', 'Starting Point'] as const,
    },
  },
  // Knowledge sources. `Surface` names where the event happened, e.g. 'Library' for the knowledge library page.
  knowledgeSource: {
    addStarted: {
      name: 'Knowledge Source: Add Started',
      properties: ['Surface'] as const,
    },
    // 'Method' is how the file is added: 'upload' or 'google_drive'.
    addMethodSelected: {
      name: 'Knowledge Source: Add Method Selected',
      properties: ['Surface', 'Method'] as const,
    },
    // An upload also sends its extension and size; a Drive create sends how many files it added.
    created: {
      name: 'Knowledge Source: Created',
      properties: ['Surface', 'Method', 'Count'] as const,
      optionalProperties: ['File Type', 'Size Bytes'] as const,
    },
    createFailed: {
      name: 'Knowledge Source: Create Failed',
      properties: ['Surface', 'Method', 'Reason'] as const,
    },
    // Connecting is shared by every surface that picks Drive files, so the event carries no surface.
    driveConnected: {
      name: 'Knowledge Source: Drive Connected',
      properties: [] as const,
    },
    deleted: {
      name: 'Knowledge Source: Deleted',
      properties: ['Surface'] as const,
    },
  },
} as const satisfies EventsShape;

export type IEvents = typeof Events;

export type EventProperties<
  Namespace extends keyof IEvents,
  E extends keyof IEvents[Namespace],
> = IEvents[Namespace][E] extends { properties: readonly (infer P)[] } ? (P extends string ? P : never) : never;
